import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './components/ui/button';
import { ArrowRight, Plus, Minus, X, Sun, Moon, Users, Package, Calendar, Gift, Coffee, FileText, Droplets, Building2, Check, Star, Truck, Clock, User, LogOut, Pause, Play, Trash2, Shield, Unlock, Sparkles, ChevronDown, MapPin, CreditCard, Smartphone, Globe, Phone, Mail, Upload, Image, ChevronUp, Leaf, Award, Heart, Zap, Filter, ShoppingBag, Tag, Lock, Download, Send, Eye, MoreVertical, RefreshCw, Home, ChevronRight, Menu, HelpCircle } from 'lucide-react';
import { donutProducts as mockDonutProducts, testimonials } from './data/mock';
import { toast, Toaster } from 'sonner';
import CookieConsent from 'react-cookie-consent';
import './App.css';

// Refactored Components
import { Header, SlideOutMenu, AnnouncementBanner } from './components/layout';
import { Footer } from './components/layout';
import { HeroSection } from './components/hero';
import { HowItWorksSection, BenefitsSection, TrustSection, TestimonialsSection, FAQSection, FinalCTASection } from './components/sections';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BRAND = {
  turquoise: '#81CBD6',
  pink: '#F39DB2',
  yellow: '#FFD16F',
};

// Donut Detail Accordion - Aufklappbare Donut-Liste für Checkout
const DonutDetailAccordion = ({ selectedDonuts, autoMix, maxDonuts, selectedDonutCount, donutPrice, donuts, t, theme, BRAND }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Erstelle Liste der ausgewählten Donuts mit Mengen
  const selectedDonutsList = Object.entries(selectedDonuts)
    .filter(([_, count]) => count > 0)
    .map(([donutId, count]) => {
      const donut = donuts.find(d => d.id === donutId);
      return donut ? { ...donut, count } : null;
    })
    .filter(Boolean);
  
  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: `1px solid ${theme.border}` }}>
      {/* Header - immer sichtbar */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 transition-all hover:bg-white/5"
        data-testid="donut-detail-toggle"
      >
        <div className="flex items-center gap-2">
          <span>{t.summary.donuts} ({autoMix ? maxDonuts : selectedDonutCount} {t.summary.pieces})</span>
          {!autoMix && selectedDonutsList.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${BRAND.turquoise}20`, color: BRAND.turquoise }}>
              {selectedDonutsList.length} Sorten
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-bold">{donutPrice.toFixed(2)}€</span>
          <div 
            className="w-6 h-6 rounded-full flex items-center justify-center transition-transform"
            style={{ 
              backgroundColor: `${BRAND.turquoise}20`,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          >
            <ChevronDown className="w-4 h-4" style={{ color: BRAND.turquoise }} />
          </div>
        </div>
      </button>
      
      {/* Aufklappbarer Inhalt */}
      <div 
        className={`overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-3 pb-3 space-y-2 max-h-72 overflow-y-auto">
          {autoMix ? (
            <div className="text-sm p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', color: theme.textMuted }}>
              🎁 Mystery Box - Überraschungsmix mit {maxDonuts} Donuts
            </div>
          ) : selectedDonutsList.length > 0 ? (
            selectedDonutsList.map(donut => (
              <div 
                key={donut.id}
                className="flex items-center justify-between p-2 rounded-lg text-sm"
                style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
              >
                <div className="flex items-center gap-2">
                  {donut.image && (
                    <img src={donut.image} alt={donut.name} className="w-8 h-8 rounded-lg object-cover" />
                  )}
                  <div>
                    <span className="font-medium">{donut.name}</span>
                    {donut.vegan && <span className="ml-1 text-xs" style={{ color: '#22c55e' }}>🌱</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: `${BRAND.pink}20`, color: BRAND.pink }}>
                    ×{donut.count}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm p-2 text-center" style={{ color: theme.textMuted }}>
              Noch keine Donuts ausgewählt
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Vollständige Übersetzungen
const TRANSLATIONS = {
  de: {
    hero: {
      badge: 'Premium Donut Lieferung für Unternehmen',
      title1: 'Donut-Abo',
      or: 'ODER',
      title2: 'Catering',
      subtitle: 'Für dein Team oder dein Event. Als flexibles Abo oder einmaliges Catering.',
      ctaAbo: '🍩 Donut-Abo starten',
      ctaCatering: '🎉 Catering anfragen',
      scroll: 'Scroll'
    },
    nav: {
      login: 'Anmelden',
      expressDelivery: 'Expresslieferung möglich',
      freshDaily: 'Täglich frisch',
      handmade: 'Handgemacht',
      sustainable: 'Nachhaltig verpackt',
    },
    howItWorks: {
      title: 'So funktioniert\'s',
      subtitle: 'In 4 Schritten zum glücklichen Team',
      step1: 'Teamgröße angeben',
      step1Desc: 'Wie viele Mitarbeiter sollen Donuts bekommen?',
      step2: 'Donuts wählen',
      step2Desc: 'Stelle deine individuelle Box aus 37 Sorten zusammen.',
      step3: 'Intervall festlegen',
      step3Desc: 'Einmalig oder regelmäßig – du bestimmst den Rhythmus.',
      step4: 'Team begeistern',
      step4Desc: 'Frische Donuts direkt ins Büro. Motivation garantiert!',
    },
    configurator: {
      aboTab: '🍩 Donut-Abo',
      cateringTab: '🎉 Catering',
      aboDesc: 'Regelmäßige Donut-Lieferung für dein Team – deutschlandweit',
      cateringDesc: 'Donuts für dein Event – Berlin & Brandenburg',
      step1Title: 'Teamgröße wählen',
      step1TitleCatering: 'Anzahl Gäste',
      persons: 'Personen',
      guests: 'Gäste',
      individualSize: 'Individuelle Größe',
      calculatedBoxes: 'Berechnete Boxen',
      boxes: 'Boxen',
      donuts: 'Donuts',
      step2Title: 'Wie möchtest du bestellen?',
      selfSelect: 'Selbst zusammenstellen',
      selfSelectDesc: 'Wähle jeden Donut einzeln aus unserem Sortiment von 37 Sorten.',
      mysteryBox: '🎁 Mystery Box',
      mysteryBoxDesc: 'Lass dich überraschen! Wir stellen die perfekte Box für dich zusammen.',
      selected: '✓ Ausgewählt',
      chooseMysteryBox: 'Wähle deine Mystery Box',
      standard: 'Classics',
      premium: 'Specials',
      deluxe: 'Dossants',
      mix: 'Mix',
      classics: 'Klassiker',
      popularVarieties: 'Beliebte Sorten',
      dossants: 'Premium Dossants',
      somethingOfEverything: 'Von allem etwas',
      selfSelectActive: '✓ Selbst zusammenstellen ausgewählt',
      selectYourDonuts: 'Wähle unten deine',
      alreadySelected: 'Bereits ausgewählt',
    },
    donutShop: {
      title: 'Wähle deine',
      titleHighlight: 'Donuts',
      subtitle: '37 handgemachte Sorten warten auf dich. Wähle',
      donutsForTeam: 'Donuts für dein Team.',
      yourSelection: 'Deine Auswahl',
      selected: 'Ausgewählt',
      add: 'Hinzufügen',
      perfect: 'Perfekt! Alle',
      donutsSelected: 'Donuts ausgewählt.',
      filterAll: 'Alle',
      filterVegan: 'Vegan',
      basis: 'Basis',
      glaze: 'Glasur',
      topping: 'Topping',
    },
    catering: {
      eventNameTitle: 'Wie heißt dein Event?',
      eventNamePlaceholder: 'z.B. Firmenjubiläum, Sommerfest, Geburtstag...',
      dateTitle: 'Wann ist dein Event?',
      minNotice: '⏰ Mindestens 48 Stunden Vorlaufzeit erforderlich',
      deliveryTitle: 'Abholung oder Lieferung?',
      pickup: 'Selbstabholung',
      pickupDesc: 'Abholung in Potsdam',
      free: 'Kostenlos',
      delivery: 'Lieferung',
      deliveryDesc: 'Berlin & Brandenburg',
      from: 'ab',
      pickupAddress: '📍 Abholadresse: Fancy Donuts, Brandenburger Str. 12, 14467 Potsdam',
      deliveryInfo: 'Lieferung nur im Raum Berlin-Brandenburg. Kosten je nach Entfernung von Potsdam (25€ - 50€).',
      bookingTitle: 'Wie möchtest du fortfahren?',
      inquiry: 'Unverbindliche Anfrage',
      inquiryDesc: 'Wir melden uns mit einem Angebot bei dir',
      directBooking: 'Direkt buchen',
      directBookingDesc: 'Verbindliche Buchung mit Anzahlung',
    },
    intervals: {
      title: 'Lieferintervall & Abo',
      once: 'Einmalig',
      weekly: 'Wöchentlich',
      biweekly: 'Alle 2 Wochen',
      monthly: 'Monatlich',
    },
    extras: {
      title: 'Extras hinzufügen',
      coffee: 'Kaffee-Paket',
      coffeeDesc: 'Italienische Pareto Kaffeebohnen',
      card: 'Personalisierte Karte',
      cardDesc: 'Mit individueller Nachricht',
      dips: 'Dip-Auswahl',
      dipsDesc: 'Premium Schokoladen-Dips (0,50€/Stk.)',
      branding: 'Firmen-Branding',
      brandingDesc: 'Logo-Sticker auf Box',
      coffeeSize350: '350g Bohnen',
      coffeeSize1kg: '1kg Bohnen',
      cardPlaceholder: 'Ihre persönliche Nachricht... (max. 300 Zeichen für A6-Karte)',
      uploadLogo: 'Logo hochladen',
      selectDips: 'Dips auswählen (0,50€ pro Stück)',
      dipQuantity: 'Menge',
      perPiece: 'pro Stück',
    },
    address: {
      title: 'Kontakt & Lieferadresse',
      company: 'Firmenname',
      companyAddress: 'Firmenadresse (Rechnungsadresse)',
      email: 'E-Mail-Adresse',
      phone: 'Telefonnummer',
      street: 'Straße',
      number: 'Nr.',
      zip: 'PLZ',
      city: 'Stadt',
    },
    summary: {
      title: 'Zusammenfassung',
      donuts: 'Donuts',
      pieces: 'Stück',
      extras: 'Extras',
      delivery: 'Lieferung',
      discount: 'Rabatt',
      total: 'Gesamt',
      orderNow: 'Jetzt bestellen',
      loginToOrder: 'Anmelden & bestellen',
      perDelivery: 'pro Lieferung',
    },
    testimonials: {
      title: 'Das sagen unsere Kunden',
      cta: 'Auch für dein Team bestellen',
    },
    faq: {
      title: 'Häufig gestellte Fragen',
      q1: 'Wie flexibel ist das Abo?',
      a1: 'Maximal flexibel! Du kannst jederzeit pausieren, das Intervall ändern oder komplett kündigen – ohne Mindestlaufzeit oder versteckte Kosten.',
      q2: 'Wann wird geliefert?',
      a2: 'Du wählst den Liefertag. Wir liefern bundesweit an Werktagen, meist zwischen 8-12 Uhr – pünktlich zum Team-Meeting oder Frühstück.',
      q3: 'Wie frisch sind die Donuts?',
      a3: 'Unsere Donuts werden am Liefertag frisch gebacken. Keine Massenproduktion, keine Konservierungsstoffe – nur Handwerk und Liebe zum Detail.',
      q4: 'Was kostet die Lieferung?',
      a4: 'Die Lieferung kostet 9,99€ pro Bestellung. Ab 100€ Bestellwert ist sie kostenlos – bei regelmäßigen Abos erreichst du das schnell.',
      q5: 'Kann ich die Donuts selbst auswählen?',
      a5: 'Absolut! Wähle aus 37 Sorten genau das, was dein Team mag. Oder lass dich mit unserer Mystery Box überraschen – wir stellen den perfekten Mix zusammen.',
      q6: 'Ist das steuerlich absetzbar?',
      a6: 'Als Betriebsausgabe für Mitarbeiterverpflegung sind die Kosten in der Regel absetzbar. Wir empfehlen Rücksprache mit deinem Steuerberater.',
    },
    cta: {
      title: 'Bereit, dein Team zu begeistern?',
      subtitle: 'Starte jetzt mit deinem ersten Donut-Abo. Keine Mindestlaufzeit, jederzeit kündbar.',
      button: 'Jetzt Abo starten',
      benefits: '✓ Kostenlos testen   ✓ Jederzeit kündbar   ✓ Zufriedenheitsgarantie',
    },
    footer: {
      company: 'Unternehmen',
      about: 'Über uns',
      careers: 'Karriere',
      press: 'Presse',
      legal: 'Rechtliches',
      agb: 'AGB',
      privacy: 'Datenschutz',
      imprint: 'Impressum',
      contact: 'Kontakt',
      phone: 'Telefon',
      email: 'E-Mail',
      address: 'Adresse',
      payment: 'Zahlungsmethoden',
      copyright: '© 2025 Fancy Donuts. Alle Rechte vorbehalten.',
      madeWith: 'Handgemacht mit ❤️ in Potsdam',
      description: 'Premium Donuts für Unternehmen. Handgemacht in Potsdam, geliefert deutschlandweit.',
    },
    checkout: {
      title: 'Bezahlung',
      selectMethod: 'Zahlungsmethode wählen',
      orderSummary: 'Bestellübersicht',
      pay: 'Jetzt bezahlen',
      back: 'Zurück',
    },
  },
  en: {
    hero: {
      badge: 'Premium Donut Delivery for Companies',
      title1: 'Donut-Abo',
      or: 'OR',
      title2: 'Catering',
      subtitle: 'For your team or your event. As a flexible subscription or one-time catering.',
      ctaAbo: '🍩 Start Donut Subscription',
      ctaCatering: '🎉 Request Catering',
      scroll: 'Scroll'
    },
    nav: {
      login: 'Sign In',
      expressDelivery: 'Express Delivery Available',
      freshDaily: 'Fresh Daily',
      handmade: 'Handmade',
      sustainable: 'Sustainably Packaged',
    },
    howItWorks: {
      title: 'How it works',
      subtitle: '4 steps to a happy team',
      step1: 'Enter team size',
      step1Desc: 'How many employees should receive donuts?',
      step2: 'Choose donuts',
      step2Desc: 'Create your individual box from 37 varieties.',
      step3: 'Set interval',
      step3Desc: 'One-time or regular – you decide the rhythm.',
      step4: 'Delight your team',
      step4Desc: 'Fresh donuts delivered to the office. Motivation guaranteed!',
    },
    configurator: {
      aboTab: '🍩 Donut Subscription',
      cateringTab: '🎉 Catering',
      aboDesc: 'Regular donut delivery for your team – nationwide',
      cateringDesc: 'Donuts for your event – Berlin & Brandenburg',
      step1Title: 'Choose team size',
      step1TitleCatering: 'Number of guests',
      persons: 'Persons',
      guests: 'Guests',
      individualSize: 'Individual size',
      calculatedBoxes: 'Calculated boxes',
      boxes: 'Boxes',
      donuts: 'Donuts',
      step2Title: 'How would you like to order?',
      selfSelect: 'Select yourself',
      selfSelectDesc: 'Choose each donut individually from our range of 37 varieties.',
      mysteryBox: '🎁 Mystery Box',
      mysteryBoxDesc: 'Be surprised! We put together the perfect box for you.',
      selected: '✓ Selected',
      chooseMysteryBox: 'Choose your Mystery Box',
      standard: 'Classics',
      premium: 'Specials',
      deluxe: 'Dossants',
      mix: 'Mix',
      classics: 'Classics',
      popularVarieties: 'Popular varieties',
      dossants: 'Premium Dossants',
      somethingOfEverything: 'Something of everything',
      selfSelectActive: '✓ Self-selection active',
      selectYourDonuts: 'Select your',
      alreadySelected: 'Already selected',
    },
    donutShop: {
      title: 'Choose your',
      titleHighlight: 'Donuts',
      subtitle: '37 handmade varieties waiting for you. Choose',
      donutsForTeam: 'donuts for your team.',
      yourSelection: 'Your selection',
      selected: 'Selected',
      add: 'Add',
      perfect: 'Perfect! All',
      donutsSelected: 'donuts selected.',
      filterAll: 'All',
      filterVegan: 'Vegan',
      basis: 'Base',
      glaze: 'Glaze',
      topping: 'Topping',
    },
    catering: {
      eventNameTitle: 'What is your event called?',
      eventNamePlaceholder: 'e.g. Company anniversary, Summer party, Birthday...',
      dateTitle: 'When is your event?',
      minNotice: '⏰ At least 48 hours notice required',
      deliveryTitle: 'Pickup or delivery?',
      pickup: 'Self-pickup',
      pickupDesc: 'Pickup in Potsdam',
      free: 'Free',
      delivery: 'Delivery',
      deliveryDesc: 'Berlin & Brandenburg',
      from: 'from',
      pickupAddress: '📍 Pickup address: Fancy Donuts, Brandenburger Str. 12, 14467 Potsdam',
      deliveryInfo: 'Delivery only in the Berlin-Brandenburg area. Costs depending on distance from Potsdam (€25 - €50).',
      bookingTitle: 'How would you like to proceed?',
      inquiry: 'Non-binding inquiry',
      inquiryDesc: 'We will contact you with an offer',
      directBooking: 'Book directly',
      directBookingDesc: 'Binding booking with deposit',
    },
    intervals: {
      title: 'Delivery interval & subscription',
      once: 'One-time',
      weekly: 'Weekly',
      biweekly: 'Every 2 weeks',
      monthly: 'Monthly',
    },
    extras: {
      title: 'Add Extras',
      coffee: 'Coffee Package',
      coffeeDesc: 'Italian Pareto Coffee Beans',
      card: 'Personalized Card',
      cardDesc: 'With individual message',
      dips: 'Dip Selection',
      dipsDesc: 'Premium chocolate dips (€0.50/pc)',
      branding: 'Company Branding',
      brandingDesc: 'Logo sticker on box',
      coffeeSize350: '350g Beans',
      coffeeSize1kg: '1kg Beans',
      cardPlaceholder: 'Your personal message... (max. 300 characters for A6 card)',
      uploadLogo: 'Upload Logo',
      selectDips: 'Select dips (€0.50 per piece)',
      dipQuantity: 'Quantity',
      perPiece: 'per piece',
    },
    address: {
      title: 'Contact & Delivery Address',
      company: 'Company name',
      companyAddress: 'Company address (billing address)',
      email: 'Email address',
      phone: 'Phone number',
      street: 'Street',
      number: 'No.',
      zip: 'ZIP',
      city: 'City',
    },
    summary: {
      title: 'Summary',
      donuts: 'Donuts',
      pieces: 'pieces',
      extras: 'Extras',
      delivery: 'Delivery',
      discount: 'Discount',
      total: 'Total',
      orderNow: 'Order now',
      loginToOrder: 'Login & order',
      perDelivery: 'per delivery',
    },
    testimonials: {
      title: 'What our customers say',
      cta: 'Order for your team too',
    },
    faq: {
      title: 'Frequently Asked Questions',
      q1: 'How flexible is the subscription?',
      a1: 'Maximum flexibility! You can pause, change the interval or cancel at any time – no minimum term or hidden costs.',
      q2: 'When is delivery?',
      a2: 'You choose the delivery day. We deliver nationwide on weekdays, usually between 8-12 am – in time for the team meeting or breakfast.',
      q3: 'How fresh are the donuts?',
      a3: 'Our donuts are freshly baked on the delivery day. No mass production, no preservatives – just craftsmanship and attention to detail.',
      q4: 'How much does delivery cost?',
      a4: 'Delivery costs €9.99 per order. From €100 order value it is free – with regular subscriptions you reach that quickly.',
      q5: 'Can I choose the donuts myself?',
      a5: 'Absolutely! Choose exactly what your team likes from 37 varieties. Or let our Mystery Box surprise you – we put together the perfect mix.',
      q6: 'Is it tax deductible?',
      a6: 'As a business expense for employee catering, the costs are usually deductible. We recommend consulting your tax advisor.',
    },
    cta: {
      title: 'Ready to delight your team?',
      subtitle: 'Start now with your first donut subscription. No minimum term, cancel anytime.',
      button: 'Start subscription now',
      benefits: '✓ Free trial   ✓ Cancel anytime   ✓ Satisfaction guarantee',
    },
    footer: {
      company: 'Company',
      about: 'About Us',
      careers: 'Careers',
      press: 'Press',
      legal: 'Legal',
      agb: 'Terms & Conditions',
      privacy: 'Privacy Policy',
      imprint: 'Imprint',
      contact: 'Contact',
      phone: 'Phone',
      email: 'Email',
      address: 'Address',
      payment: 'Payment Methods',
      copyright: '© 2025 Fancy Donuts. All rights reserved.',
      madeWith: 'Handmade with ❤️ in Potsdam',
      description: 'Premium donuts for businesses. Handmade in Potsdam, delivered throughout Germany.',
    },
    checkout: {
      title: 'Payment',
      selectMethod: 'Select payment method',
      orderSummary: 'Order summary',
      pay: 'Pay now',
      back: 'Back',
    },
  }
};

// Dip Options - mit Preis pro Stück
const DIP_OPTIONS = [
  { id: 'chocolate', name: { de: 'Schokolade', en: 'Chocolate' }, color: '#5D4037', pricePerPiece: 0.50 },
  { id: 'lotus', name: { de: 'Lotus', en: 'Lotus' }, color: '#D4A574', pricePerPiece: 0.50 },
  { id: 'caramel', name: { de: 'Karamell', en: 'Caramel' }, color: '#C68B59', pricePerPiece: 0.50 },
  { id: 'white_chocolate', name: { de: 'Weiße Schokolade', en: 'White Chocolate' }, color: '#FFF8E7', pricePerPiece: 0.50 },
  { id: 'dark_chocolate', name: { de: 'Dunkle Schokolade', en: 'Dark Chocolate' }, color: '#3E2723', pricePerPiece: 0.50 },
  { id: 'pistachio', name: { de: 'Pistazie', en: 'Pistachio' }, color: '#93C572', pricePerPiece: 0.50 },
];

// Preiskategorien
const PRICE_CATEGORIES = [
  { id: 'standard', name: 'Classics', price: 3.00, description: 'Klassische Donuts' },
  { id: 'premium', name: 'Specials', price: 4.00, description: 'Mit besonderen Toppings' },
  { id: 'deluxe', name: 'Dossants', price: 5.00, description: 'Premium Croissant-Donuts' },
];

const DONUTS_PER_BOX = 6;
const DELIVERY_COST_PER_BOX = 0; // TEMPORÄR 0€ für Test

// Extras - mit Preisanzeige
const EXTRAS = [
  { id: 'coffee', name: 'Kaffee-Paket', desc: 'Italienische Pareto Kaffeebohnen', price: 15.00, priceDisplay: 'ab 15€', icon: Coffee },
  { id: 'card', name: 'Personalisierte Karte', desc: 'Mit individueller Nachricht (A6)', price: 3.00, priceDisplay: '3€', icon: FileText },
  { id: 'dips', name: 'Dip-Auswahl', desc: 'Premium Schokoladen-Dips (0,50€/Stk.)', price: 0.50, priceDisplay: '0,50€/Stk.', icon: Droplets },
  { id: 'branding', name: 'Firmen-Branding', desc: 'Logo auf jedem Donut (1€/Stk.)', price: 1.00, priceDisplay: '1€/Donut', icon: Building2, perDonut: true },
];

// Abo-Rabatte
const INTERVALS = [
  { id: 'once', name: 'Einmalig', discount: 0 },
  { id: 'weekly', name: 'Wöchentlich', discount: 15 },
  { id: 'biweekly', name: 'Alle 2 Wochen', discount: 10 },
  { id: 'monthly', name: 'Monatlich', discount: 5 },
];

const TEAM_SIZES = [5, 10, 15, 20, 30, 50, 100, '100+'];

function App() {
  // DARK MODE ONLY - Light Mode entfernt
  const darkMode = true;
  const [scrollY, setScrollY] = useState(0);
  
  // Products State - laden aus Datenbank mit Fallback auf Mock
  const [donutProducts, setDonutProducts] = useState(mockDonutProducts);
  const [productsLoaded, setProductsLoaded] = useState(false);
  
  // Legal Pages State
  const [legalPage, setLegalPage] = useState(null); // 'impressum', 'datenschutz', 'agb', 'widerruf'
  
  // Gamification - Glücksrad State
  const [showSpinWheel, setShowSpinWheel] = useState(false);
  const [wheelSpinning, setWheelSpinning] = useState(false);
  const [wheelResult, setWheelResult] = useState(null);
  const [wheelEmail, setWheelEmail] = useState('');
  const [hasSpun, setHasSpun] = useState(localStorage.getItem('hasSpun') === 'true');
  
  // Auth
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showDashboard, setShowDashboard] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Konfigurator State
  const [teamSize, setTeamSize] = useState(20);
  const [customTeamSize, setCustomTeamSize] = useState(''); // Für 100+ Eingabe
  const [extendedSlider, setExtendedSlider] = useState(false); // Erweiterte Spanne für 100+
  const [boxCount, setBoxCount] = useState(4);
  const [boxGlowing, setBoxGlowing] = useState(false); // Glow-Animation für Boxen-Berechnung
  const [showConfetti, setShowConfetti] = useState(false); // Konfetti wenn alle Donuts ausgewählt
  const [autoMix, setAutoMix] = useState(false);
  const [priceCategory, setPriceCategory] = useState('standard');
  const [selectedDonuts, setSelectedDonuts] = useState({});
  const [interval, setInterval] = useState('once');
  const [selectedExtras, setSelectedExtras] = useState([]);
  
  // Adresse & Kontakt
  const [address, setAddress] = useState({ company: '', companyAddress: '', street: '', number: '', zip: '', city: '', email: '', phone: '' });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  
  // UI State
  const [currentView, setCurrentView] = useState('home');
  const [activeDonutCategory, setActiveDonutCategory] = useState('Alle');
  const [openFaq, setOpenFaq] = useState(null);
  const [orderMode, setOrderMode] = useState('abo'); // 'abo' oder 'catering'
  const [cateringDate, setCateringDate] = useState('');
  const [cateringDelivery, setCateringDelivery] = useState('pickup'); // 'pickup' oder 'delivery'
  const [cateringEventName, setCateringEventName] = useState('');
  const [cateringBookingType, setCateringBookingType] = useState('inquiry'); // 'booking' oder 'inquiry'
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null); // 'apple_pay', 'paypal', 'card'
  const [dashboardView, setDashboardView] = useState('main'); // 'main', 'profile', 'payment', 'addresses', 'contact', 'admin'
  const [editProfile, setEditProfile] = useState({ company_name: '', contact_person: '', email: '' });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  
  // Admin State - Separates Admin-System
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  
  // Admin-Zugang über URL - useEffect für sichere Client-Side Detection
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname.includes('/admin') || params.get('admin') === 'true') {
      setShowAdminLogin(true);
    }
  }, []);
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken') || null);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminData, setAdminData] = useState(null);
  const [adminView, setAdminView] = useState('overview'); // 'overview', 'users', 'orders', 'newsletter'
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');
  
  // Legacy - wird nicht mehr gebraucht
  const [isAdmin, setIsAdmin] = useState(false);
  
  const configuratorRef = useRef(null);
  const faqRef = useRef(null);
  const intervalRef = useRef(null);
  
  // Language & Extras State
  const [language, setLanguage] = useState('de');
  const t = TRANSLATIONS[language];
  
  // Extras Detail States
  const [showExtraModal, setShowExtraModal] = useState(null); // 'coffee', 'card', 'dips', 'branding'
  const [coffeeSize, setCoffeeSize] = useState('350g'); // '350g' oder '1kg'
  const [cardMessage, setCardMessage] = useState('');
  const [dipQuantities, setDipQuantities] = useState({}); // { chocolate: 2, lotus: 1, ... }
  const [brandingLogo, setBrandingLogo] = useState(null);
  const [brandingLogoPreview, setBrandingLogoPreview] = useState(null);
  
  // Filter State
  const [veganFilter, setVeganFilter] = useState(false);
  
  // Scroll to Top visibility
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Theme
  const theme = darkMode ? {
    bg: '#0a0a0a', bgSecondary: '#111', bgCard: '#1a1a1a',
    text: '#ffffff', textMuted: '#9ca3af', border: 'rgba(255,255,255,0.1)',
  } : {
    bg: '#ffffff', bgSecondary: '#f8fafc', bgCard: '#ffffff',
    text: '#111827', textMuted: '#6b7280', border: 'rgba(0,0,0,0.08)',
  };

  // Load products from database
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products/`);
        const data = await res.json();
        if (data.products && data.products.length > 0) {
          // Map database products to match expected format
          const dbProducts = data.products.map(p => ({
            id: p.id,
            name: p.name,
            category: p.category,
            price: p.price,
            image: p.image,
            basis: p.basis,
            glasur: p.glasur,
            topping: p.topping,
            sauce: p.sauce,
            vegan: p.vegan,
            description: p.description
          }));
          setDonutProducts(dbProducts);
          console.log(`Loaded ${dbProducts.length} products from database`);
        } else {
          console.log('Using mock products (database empty)');
        }
      } catch (err) {
        console.log('Using mock products (fetch error):', err.message);
      } finally {
        setProductsLoaded(true);
      }
    };
    loadProducts();
  }, []);

  // Donut-Kategorien für Filter - alle Kategorien von my.fancy-donuts.com
  const donutCategories = ['Alle', 'Classics', 'Specials', 'Premium', 'Boxen'];
  
  // Sortierung: Classics zuerst, dann Specials, Dossants, Berliner
  const categoryOrder = { 'Classics': 1, 'Specials': 2, 'Dossants': 3, 'Berliner': 4 };
  const sortedDonuts = [...donutProducts].sort((a, b) => (categoryOrder[a.category] || 99) - (categoryOrder[b.category] || 99));
  
  // Filter nach Kategorie UND Vegan
  const filteredDonuts = sortedDonuts
    .filter(d => {
      if (activeDonutCategory === 'Alle') return true;
      if (activeDonutCategory === 'Boxen') return d.isBox === true; // Für vordefinierte Boxen
      return d.category === activeDonutCategory;
    })
    .filter(d => !veganFilter || d.vegan === true);
  
  // Dip-Preis berechnen
  const calculateDipPrice = () => {
    return Object.values(dipQuantities).reduce((sum, qty) => sum + (qty * 0.50), 0);
  };

  // Berechnungen
  const selectedDonutCount = Object.values(selectedDonuts).reduce((sum, qty) => sum + qty, 0);
  const maxDonuts = boxCount * DONUTS_PER_BOX;
  
  const calculateDonutPrice = () => {
    if (autoMix) {
      const categoryPrice = PRICE_CATEGORIES.find(c => c.id === priceCategory)?.price || 3.00;
      return maxDonuts * categoryPrice;
    } else {
      return Object.entries(selectedDonuts).reduce((sum, [id, qty]) => {
        const donut = donutProducts.find(d => d.id === id);
        return sum + (donut?.price || 3.50) * qty;
      }, 0);
    }
  };

  const donutPrice = calculateDonutPrice();
  const extrasPrice = selectedExtras.reduce((sum, id) => {
    const extra = EXTRAS.find(e => e.id === id);
    // Für Dips: verwende berechneten Preis
    if (id === 'dips') {
      return sum + calculateDipPrice();
    }
    // Für Kaffee: prüfe Größe
    if (id === 'coffee') {
      return sum + (coffeeSize === '1kg' ? 35 : 15);
    }
    // Für Branding: 1€ pro Donut
    if (id === 'branding') {
      return sum + (maxDonuts * 1.00);
    }
    return sum + (extra?.price || 0);
  }, 0);
  const deliveryCost = DELIVERY_COST_PER_BOX * boxCount; // Versand pro Box
  const subtotal = donutPrice + extrasPrice + deliveryCost;
  const discountPercent = INTERVALS.find(i => i.id === interval)?.discount || 0;
  const discount = subtotal * (discountPercent / 100);
  const total = subtotal - discount;

  // Hero Donuts - EXTRA GROSS
  const floatingDonuts = [
    { id: 'SD_005', x: '-8%', y: '5%', size: 580, rotateX: 35, rotateY: 25, rotateZ: -20 },
    { id: 'CD_008', x: '70%', y: '5%', size: 520, rotateX: 45, rotateY: -20, rotateZ: 15 },
  ];

  // Zusätzliche schwebende Donuts für andere Sektionen - DEAKTIVIERT
  const sectionDonuts = {
    howItWorks: [],
    testimonials: [],
    faq: [],
    cta: []
  };

  // Helper Funktion für schwebende Donuts - MIT ANIMATION
  const renderFloatingDonuts = (donuts, parallaxFactor = 0.05) => {
    return donuts.map((d, i) => {
      const product = donutProducts.find(p => p.id === d.id);
      return product && (
        <div key={`${d.id}-${i}`} className="absolute pointer-events-none hidden lg:block" 
          style={{ 
            left: d.x, 
            top: d.y, 
            perspective: '1000px', 
            transform: `translateY(${scrollY * parallaxFactor}px)`,
            zIndex: 1
          }}>
          <img src={product.image} alt="" className="object-contain"
            style={{ 
              width: d.size, 
              height: d.size, 
              transform: `rotateX(${d.rotateX}deg) rotateY(${d.rotateY}deg) rotateZ(${d.rotateZ}deg)`,
              filter: darkMode ? 'drop-shadow(0 30px 60px rgba(0,0,0,0.5))' : 'drop-shadow(0 25px 50px rgba(0,0,0,0.2))', 
              animation: `floatDonut ${4 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.8}s`
            }} />
        </div>
      );
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (token) fetchUser();
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname.includes('/checkout/success')) setCurrentView('success');
    if (window.location.pathname.includes('/checkout/cancel')) setCurrentView('cancel');
    // Admin-Zugang über /admin URL oder ?admin=true Query-Parameter
    if (window.location.pathname === '/admin' || 
        window.location.pathname === '/admin/' ||
        window.location.pathname.includes('/admin') ||
        params.get('admin') === 'true') {
      setShowAdminLogin(true);
    }
  }, []);

  // Glücksrad wird jetzt nur vor dem Checkout angezeigt - nicht automatisch
  // Das Popup wird in handleOrder() oder beim Checkout-Button getriggert
  // useEffect entfernt, um kein automatisches Popup nach 5 Sekunden zu zeigen

  // Glücksrad Spin Funktion
  const spinWheel = async () => {
    if (!wheelEmail || wheelSpinning) return;
    
    setWheelSpinning(true);
    
    // Mögliche Gewinne - bessere Verteilung
    const prizes = [
      { text: '5% Rabatt', code: 'FANCY5', discount: 5, segment: 0 },
      { text: '10% Rabatt', code: 'FANCY10', discount: 10, segment: 1 },
      { text: '5% Rabatt', code: 'LUCKY5', discount: 5, segment: 2 },
      { text: '15% Rabatt', code: 'MEGA15', discount: 15, segment: 3 },
      { text: '5% Rabatt', code: 'SWEET5', discount: 5, segment: 4 },
      { text: '10% Rabatt', code: 'DONUT10', discount: 10, segment: 5 },
    ];
    
    // Zufälliger Gewinn
    const winIndex = Math.floor(Math.random() * prizes.length);
    const prize = prizes[winIndex];
    
    // E-Mail an Backend senden für Newsletter
    try {
      await fetch(`${API_URL}/api/email/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: wheelEmail,
          discount_code: prize.code,
          discount_percent: prize.discount,
          source: 'spin_wheel'
        })
      });
    } catch (err) {
      console.log('Newsletter save failed:', err);
    }
    
    // Nach 4 Sekunden Ergebnis anzeigen
    setTimeout(() => {
      setWheelResult(prize);
      setWheelSpinning(false);
      setHasSpun(true);
      localStorage.setItem('hasSpun', 'true');
      localStorage.setItem('discountCode', prize.code);
      localStorage.setItem('discountPercent', prize.discount.toString());
    }, 4000);
  };
  
  // Scroll to Top Funktion
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Automatisch zu Schritt 3 scrollen wenn maximale Donut-Anzahl erreicht + Konfetti
  useEffect(() => {
    if (selectedDonutCount >= maxDonuts && !autoMix && intervalRef.current) {
      // Konfetti-Animation starten
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000); // Konfetti für 3 Sekunden
      
      // SCHNELL zu Schritt 3 springen - kurze Verzögerung damit User Toast sieht
      const timer = setTimeout(() => {
        const element = intervalRef.current;
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const targetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        // Schneller Scroll (500ms) - direkter Sprung
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
        
        toast.success('Perfekt! Alle Donuts ausgewählt. Weiter zu Schritt 3!', { icon: '🎉' });
      }, 800); // Nur 0.8 Sekunden warten
      return () => clearTimeout(timer);
    }
  }, [selectedDonutCount, maxDonuts, autoMix]);

  // Adress-Autocomplete mit OpenStreetMap Photon (kostenlos, kein API-Key)
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const response = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5&lang=de&lat=52.4&lon=13.0`
      );
      const data = await response.json();
      const suggestions = data.features?.map(f => ({
        street: f.properties.street || '',
        number: f.properties.housenumber || '',
        zip: f.properties.postcode || '',
        city: f.properties.city || f.properties.town || f.properties.village || '',
        country: f.properties.country || '',
        name: f.properties.name || '',
        display: [
          f.properties.name,
          f.properties.street,
          f.properties.housenumber,
          f.properties.postcode,
          f.properties.city || f.properties.town || f.properties.village
        ].filter(Boolean).join(', ')
      })) || [];
      setAddressSuggestions(suggestions);
    } catch (e) {
      console.error('Address search error:', e);
      setAddressSuggestions([]);
    }
  };

  const selectAddressSuggestion = (suggestion) => {
    setAddress(prev => ({
      ...prev,
      // Übernimm den Firmennamen wenn vorhanden
      company: suggestion.name && suggestion.name !== suggestion.street ? suggestion.name : prev.company,
      street: suggestion.street || '',
      number: suggestion.number || '',
      zip: suggestion.zip || '',
      city: suggestion.city || ''
    }));
    setAddressSuggestions([]);
  };

  // API
  const apiCall = async (endpoint, options = {}) => {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
    
    // Lese die Response als Text, um sie dann zu parsen
    const text = await res.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = { detail: text || 'Unbekannter Fehler' };
    }
    
    if (!res.ok) {
      const errorMessage = data.detail || data.message || 'Ein Fehler ist aufgetreten';
      throw new Error(errorMessage);
    }
    return data;
  };

  const fetchUser = async () => {
    try {
      const data = await apiCall('/api/user/me');
      setUser(data);
    } catch {
      localStorage.removeItem('token');
      setToken(null);
    }
  };

  const handleLogin = async (email, password) => {
    try {
      const data = await apiCall('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
      localStorage.setItem('token', data.token);
      setToken(data.token);
      setUser(data.user);
      setShowAuth(false);
      toast.success('Erfolgreich angemeldet!');
    } catch (e) { toast.error(e.message); }
  };

  const handleRegister = async (email, password, company, contact) => {
    try {
      // Direkt fetch verwenden für bessere Fehlerbehandlung
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, company_name: company, contact_name: contact })
      });
      
      // Bei 400 Bad Request ist es wahrscheinlich "E-Mail bereits registriert"
      if (res.status === 400) {
        toast.error('Diese E-Mail ist bereits registriert. Bitte melden Sie sich an.');
        return;
      }
      
      if (!res.ok) {
        toast.error('Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
        return;
      }
      
      let data;
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : {};
      } catch {
        toast.error('Unerwartete Antwort vom Server');
        return;
      }
      
      if (data.token) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        setShowAuth(false);
        toast.success('Konto erstellt!');
      } else {
        toast.error('Unerwartete Antwort vom Server');
      }
    } catch (e) { 
      console.error('Register error:', e);
      toast.error('Verbindungsfehler. Bitte versuchen Sie es erneut.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setShowDashboard(false);
  };

  const fetchDashboard = async () => {
    try {
      const data = await apiCall('/api/user/dashboard');
      
      // Auch Stripe Invoices laden für PDF Downloads
      try {
        const stripeData = await apiCall('/api/payments/stripe-invoices');
        if (stripeData.invoices && stripeData.invoices.length > 0) {
          // Merge Stripe invoice data (with pdf_url) into dashboard data
          data.invoices = stripeData.invoices;
        }
      } catch (stripeErr) {
        console.log('No Stripe invoices available');
      }
      
      // Zahlungsmethoden laden
      try {
        const pmData = await apiCall('/api/payments/payment-methods');
        if (pmData.payment_methods) {
          setSavedPaymentMethods(pmData.payment_methods.map(pm => ({
            id: pm.id,
            brand: pm.brand?.charAt(0).toUpperCase() + pm.brand?.slice(1),
            last4: pm.last4,
            exp: `${pm.exp_month}/${pm.exp_year}`,
            isDefault: pm.is_default
          })));
        }
      } catch (pmErr) {
        console.log('No saved payment methods');
      }
      
      setDashboardData(data);
      
      // Check if user is admin
      if (user?.email === 'ceo@print-coffee.com' || user?.email === 'admin@fancy-donuts.shop') {
        setIsAdmin(true);
      }
    } catch (e) { toast.error(e.message); }
  };

  // Admin Funktionen - Separates System
  const adminApiCall = async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
      'Authorization': `Bearer ${adminToken}`
    };
    const res = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Admin API Fehler');
    }
    return res.json();
  };

  const handleAdminLogin = async () => {
    setAdminLoading(true);
    setAdminError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adminEmail, password: adminPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login fehlgeschlagen');
      
      localStorage.setItem('adminToken', data.token);
      setAdminToken(data.token);
      setAdminLoggedIn(true);
      setShowAdminLogin(false);
      toast.success('Admin-Login erfolgreich!');
      
      // Lade Admin-Daten
      fetchAdminData(data.token);
    } catch (e) {
      setAdminError(e.message);
      toast.error(e.message);
    }
    setAdminLoading(false);
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('adminToken');
    setAdminToken(null);
    setAdminLoggedIn(false);
    setAdminData(null);
    setShowAdminLogin(false);
    toast.success('Admin abgemeldet');
  };

  const fetchAdminData = async (token = adminToken) => {
    if (!token) return;
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [stats, users, orders, newsletter] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/users`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/orders`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/newsletter`, { headers }).then(r => r.json())
      ]);
      setAdminData({ stats, users: users.users, orders: orders.orders, newsletter: newsletter.subscribers });
      setAdminLoggedIn(true);
    } catch (e) {
      console.error('Admin data fetch failed:', e);
      handleAdminLogout();
    }
  };

  // Admin: Kunde löschen
  const handleAdminDeleteUser = async (userId, email) => {
    if (!confirm(`Wirklich ${email} löschen? Alle Bestellungen werden auch gelöscht!`)) return;
    try {
      await adminApiCall(`/api/admin/users/${userId}`, { method: 'DELETE' });
      toast.success('Kunde gelöscht');
      fetchAdminData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Admin: Bestellung löschen
  const handleAdminDeleteOrder = async (orderId) => {
    if (!confirm('Wirklich diese Bestellung löschen?')) return;
    try {
      await adminApiCall(`/api/admin/orders/${orderId}`, { method: 'DELETE' });
      toast.success('Bestellung gelöscht');
      fetchAdminData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Admin: Bestellstatus ändern
  const handleAdminUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await adminApiCall(`/api/admin/orders/${orderId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });
      toast.success(`Status auf "${newStatus}" geändert`);
      fetchAdminData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Admin: Mitarbeiter benachrichtigen
  const handleAdminNotifyStaff = async (orderId) => {
    try {
      await adminApiCall(`/api/admin/orders/${orderId}/notify-staff`, { method: 'POST' });
      toast.success('Mitarbeiter benachrichtigt!');
      fetchAdminData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Admin: CSV Export
  const handleAdminExportOrders = async () => {
    try {
      const data = await adminApiCall('/api/admin/export/orders');
      const blob = new Blob([data.content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
      toast.success(`${data.total_orders} Bestellungen exportiert`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleAdminExportNewsletter = async () => {
    try {
      const data = await adminApiCall('/api/admin/export/newsletter');
      const blob = new Blob([data.content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
      toast.success(`${data.total} E-Mails exportiert`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Prüfe Admin-Token beim Laden
  useEffect(() => {
    if (adminToken) {
      fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      }).then(r => {
        if (r.ok) {
          setAdminLoggedIn(true);
          fetchAdminData(adminToken);
        } else {
          handleAdminLogout();
        }
      }).catch(() => handleAdminLogout());
    }
  }, []);

  const scrollToConfigurator = (mode = null) => {
    if (mode) setOrderMode(mode);
    // Mehr Offset damit Tabs nicht am Header kleben - größerer Abstand für Mobile
    const element = configuratorRef.current;
    if (element) {
      const headerOffset = 140; // Größerer Abstand zum Header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const scrollToFaq = () => {
    setShowDashboard(false);
    setTimeout(() => {
      faqRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const openDashboardSection = (section) => {
    setDashboardView(section);
    if (user) {
      setEditProfile({
        company_name: user.company_name || '',
        contact_person: user.contact_person || '',
        email: user.email || ''
      });
    }
  };

  const saveProfile = async () => {
    try {
      await apiCall('/api/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editProfile)
      });
      toast.success('Profil gespeichert!');
      setDashboardView('main');
      fetchDashboard();
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
  };

  const toggleExtra = (id) => {
    setSelectedExtras(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  };

  const addDonut = (id) => {
    if (selectedDonutCount >= maxDonuts) {
      toast.success(`Perfekt! Du hast alle ${maxDonuts} Donuts ausgewählt`, { icon: '🎉' });
      return;
    }
    setSelectedDonuts(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  };

  const removeDonut = (id) => {
    setSelectedDonuts(prev => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) - 1) }));
  };

  const handleOrder = async () => {
    // PFLICHT: User muss eingeloggt sein - kein Gast-Checkout
    if (!user) {
      toast.error('Bitte melde dich an oder erstelle ein Konto');
      setAuthMode('register');
      setShowAuth(true);
      setShowCheckout(false);
      return;
    }
    
    if (!address.company || !address.street || !address.zip || !address.city) {
      toast.error('Bitte alle Adressfelder ausfüllen');
      return;
    }
    if (!autoMix && selectedDonutCount < maxDonuts) {
      toast.error(`Bitte ${maxDonuts} Donuts auswählen (aktuell: ${selectedDonutCount})`);
      return;
    }

    try {
      let items = [];
      if (autoMix) {
        const categoryPrice = PRICE_CATEGORIES.find(c => c.id === priceCategory)?.price || 3.00;
        items.push({ product_id: 'auto_mix', name: `${PRICE_CATEGORIES.find(c => c.id === priceCategory)?.name} Donuts`, quantity: maxDonuts, price: categoryPrice });
      } else {
        Object.entries(selectedDonuts).filter(([_, qty]) => qty > 0).forEach(([id, qty]) => {
          const donut = donutProducts.find(d => d.id === id);
          if (donut) items.push({ product_id: id, name: donut.name, quantity: qty, price: donut.price });
        });
      }
      
      selectedExtras.forEach(id => {
        const extra = EXTRAS.find(e => e.id === id);
        if (id === 'dips') {
          const totalDipCount = Object.values(dipQuantities).reduce((sum, qty) => sum + qty, 0);
          if (totalDipCount > 0) {
            items.push({ product_id: id, name: extra.name, quantity: totalDipCount, price: 0.50 });
          }
        } else if (id === 'coffee') {
          const coffeePrice = coffeeSize === '1kg' ? 35 : 15;
          items.push({ product_id: id, name: `${extra.name} (${coffeeSize})`, quantity: 1, price: coffeePrice });
        } else if (id === 'branding') {
          items.push({ product_id: id, name: `${extra.name} (${maxDonuts} Donuts)`, quantity: maxDonuts, price: 1.00 });
        } else if (extra) {
          items.push({ product_id: id, name: extra.name, quantity: 1, price: extra.price });
        }
      });

      items.push({ product_id: 'delivery', name: `Lieferung (${boxCount} ${boxCount === 1 ? 'Box' : 'Boxen'})`, quantity: boxCount, price: DELIVERY_COST_PER_BOX });

      const fullAddress = `${address.company}, ${address.street} ${address.number}, ${address.zip} ${address.city}`;
      
      // Strukturierte Adressdaten für Stripe
      const addressDetails = {
        company: address.company,
        street: address.street,
        number: address.number,
        zip: address.zip,
        city: address.city
      };
      
      // Verwende den aktuellen Token (könnte gerade erstellt worden sein)
      const currentToken = localStorage.getItem('token');
      const order = await fetch(`${API_URL}/api/orders/create`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({
          items, 
          delivery_address: fullAddress,
          address_details: addressDetails,
          is_subscription: interval !== 'once',
          subscription_interval: interval !== 'once' ? interval : null
        })
      }).then(r => r.json());

      if (!order.id) {
        toast.error('Bestellung konnte nicht erstellt werden');
        return;
      }

      const checkout = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({ order_id: order.id, origin_url: window.location.origin })
      }).then(r => r.json());

      if (checkout.checkout_url) {
        window.location.href = checkout.checkout_url;
      } else {
        toast.error('Checkout konnte nicht gestartet werden');
      }
    } catch (e) { toast.error(e.message); }
  };

  // Legal Pages Content
  const legalContent = {
    impressum: {
      title: 'Impressum',
      sections: [
        {
          title: 'Anschrift (Hauptsitz)',
          content: `Fancy Donuts
Charlottenstraße 116
14467 Potsdam`
        },
        {
          title: 'Vertreten durch',
          content: 'Jay Adel'
        },
        {
          title: 'Kontakt',
          content: `Telefon: 033 123 54 77 54
E-Mail: info@fancy-donuts.com`
        },
        {
          title: 'Registereintrag',
          content: `Eintragung im Handelsregister.
Registergericht: Landeshauptstadt Potsdam`
        },
        {
          title: 'Umsatzsteuer',
          content: `Umsatzsteuer-Identifikationsnummer gemäß
§27 a Umsatzsteuergesetz: 046/21203318`
        },
        {
          title: 'Verantwortlicher für den Inhalt',
          content: 'Jay Adel (Anschrift wie oben)'
        },
        {
          title: 'Streitschlichtung',
          content: `Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr

Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.`
        },
        {
          title: 'Haftung für Inhalte',
          content: 'Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.'
        },
        {
          title: 'Haftung für Links',
          content: 'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen.'
        },
        {
          title: 'Urheberrecht',
          content: 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.'
        }
      ]
    },
    datenschutz: {
      title: 'Datenschutzerklärung',
      sections: [
        {
          title: '1. Datenschutz auf einen Blick',
          content: `Diese Datenschutzerklärung klärt Sie über die Art, den Umfang und Zweck der Verarbeitung von personenbezogenen Daten innerhalb unseres Onlineangebotes auf.

Verantwortlicher: Fancy Donuts, Charlottenstraße 116, 14467 Potsdam
E-Mail: info@fancy-donuts.com`
        },
        {
          title: '2. Datenerfassung auf unserer Website',
          content: `Wir erheben personenbezogene Daten, wenn Sie uns diese im Rahmen einer Bestellung, einer Kontaktaufnahme oder bei der Registrierung freiwillig mitteilen.

Hierzu zählen: Name, E-Mail-Adresse, Firmenname, Lieferadresse, Telefonnummer und Zahlungsinformationen.`
        },
        {
          title: '3. Bestellabwicklung',
          content: `Die von Ihnen bei der Bestellung angegebenen Daten werden ausschließlich zur Abwicklung des Kaufvertrages verwendet. Nach vollständiger Abwicklung des Vertrages werden Ihre Daten für die weitere Verwendung gesperrt und nach Ablauf der steuer- und handelsrechtlichen Vorschriften gelöscht.`
        },
        {
          title: '4. Cookies',
          content: `Unsere Website verwendet Cookies. Dabei handelt es sich um kleine Textdateien, die auf Ihrem Endgerät gespeichert werden. Sie können die Speicherung von Cookies in Ihrem Browser deaktivieren, allerdings kann dies die Funktionalität der Website einschränken.`
        },
        {
          title: '5. Zahlungsdienstleister',
          content: `Für die Zahlungsabwicklung nutzen wir den Dienst Stripe. Dabei werden Ihre Zahlungsdaten direkt an Stripe übermittelt. Stripe ist unter dem Privacy-Shield-Abkommen zertifiziert.`
        },
        {
          title: '6. Ihre Rechte',
          content: `Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger sowie den Zweck der Datenverarbeitung. Sie haben außerdem ein Recht auf Berichtigung, Sperrung oder Löschung dieser Daten.

Kontakt: info@fancy-donuts.com`
        },
        {
          title: '7. SSL-Verschlüsselung',
          content: 'Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte eine SSL-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von "http://" auf "https://" wechselt.'
        }
      ]
    },
    agb: {
      title: 'Allgemeine Geschäftsbedingungen (AGB)',
      sections: [
        {
          title: '1. Geltungsbereich',
          content: `Diese Allgemeinen Geschäftsbedingungen gelten für alle Bestellungen, die über unseren Online-Shop getätigt werden.

Anbieter: Fancy Donuts, Charlottenstraße 116, 14467 Potsdam`
        },
        {
          title: '2. Vertragsschluss',
          content: `Die Darstellung der Produkte im Online-Shop stellt kein rechtlich bindendes Angebot dar. Durch Anklicken des Buttons "Jetzt kaufen" geben Sie eine verbindliche Bestellung ab. Die Bestätigung des Zugangs Ihrer Bestellung erfolgt per E-Mail unmittelbar nach dem Absenden.`
        },
        {
          title: '3. Lieferung & Versand',
          content: `Wir liefern deutschlandweit mit DHL Express. Die Lieferung erfolgt in der Regel am nächsten Werktag bei Bestellung vor 9:00 Uhr.

Versandkosten:
• Standardversand: 9,99€
• Expresslieferung (24h): ab 14,99€
• Kostenlose Lieferung ab einem Bestellwert von 50€

Für das Catering in Berlin & Brandenburg gelten separate Lieferkonditionen (25€ - 50€ je nach Entfernung).`
        },
        {
          title: '4. Frischegarantie',
          content: `Unsere Donuts werden täglich frisch handgemacht und am Tag des Versands produziert. Bei korrekter Lagerung (kühl und trocken) sind sie bis zu 3 Tage haltbar.

Unsere Garantie: Sollten Ihre Donuts nicht innerhalb eines Werktages nach Versand ankommen oder während des Transports stark beschädigt worden sein, senden wir Ihnen kostenlos eine neue Box zu!`
        },
        {
          title: '5. Widerrufsrecht für Lebensmittel',
          content: `WICHTIG: Bei Lebensmitteln, die schnell verderben können, besteht gemäß § 312g Abs. 2 Nr. 2 BGB KEIN Widerrufsrecht.

Da unsere Donuts frisch zubereitet werden und verderbliche Waren sind, ist ein Widerruf nach Versand nicht möglich. Bitte prüfen Sie Ihre Bestellung sorgfältig vor dem Kauf.`
        },
        {
          title: '6. Stornierung',
          content: `Stornierungen sind nur bis zum Tag vor dem Versand möglich. Am Versandtag selbst werden die Bestellungen bereits früh morgens vorbereitet und können nicht mehr storniert werden.

Für Stornierungen kontaktieren Sie uns bitte unter info@fancy-donuts.com`
        },
        {
          title: '7. Preise & Zahlung',
          content: `Alle Preise verstehen sich in Euro inklusive der gesetzlichen Mehrwertsteuer.

Zahlungsmethoden:
• Kreditkarte (Visa, Mastercard, American Express)
• Apple Pay
• PayPal
• SEPA Lastschrift`
        },
        {
          title: '8. Allergene & Inhaltsstoffe',
          content: `WICHTIGER HINWEIS für Allergiker: Unsere Donuts werden in einer Küche hergestellt, in der Nüsse, Milch, Eier, Weizen und andere Allergene verarbeitet werden. Trotz sorgfältiger Reinigung können Spuren von Allergenen enthalten sein.

Bei schweren Allergien empfehlen wir, von einer Bestellung abzusehen.`
        },
        {
          title: '9. Hygiene & Qualität',
          content: `Wir unterliegen den strengen deutschen Lebensmittelvorschriften und werden regelmäßig vom Gesundheitsamt kontrolliert. Alle Mitarbeiter sind entsprechend geschult und halten höchste Hygienestandards ein.

Unsere Donuts werden:
• Täglich frisch aus frischem Teig hergestellt
• Ohne Konservierungsstoffe produziert
• In lebensmittelechten Verpackungen versandt`
        },
        {
          title: '10. Gewährleistung',
          content: `Bei Beschädigungen während des Transports bitten wir Sie, uns innerhalb von 24 Stunden nach Erhalt mit Fotos der Ware und der Verpackung zu kontaktieren. Wir werden den Fall dann mit DHL klären und Ihnen schnellstmöglich Ersatz liefern.`
        },
        {
          title: '11. Abo-Bedingungen',
          content: `Bei Abschluss eines Donut-Abos gilt:
• Das Abo ist jederzeit kündbar (zum nächsten Liefertermin)
• Rabatte: Wöchentlich 15%, Alle 2 Wochen 10%, Monatlich 5%
• Pausierung des Abos ist jederzeit möglich
• Die Zahlungsintervalle entsprechen den Lieferintervallen`
        },
        {
          title: '12. Schlussbestimmungen',
          content: `Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Potsdam, sofern der Kunde Kaufmann ist.

Stand: Februar 2025`
        }
      ]
    },
    widerruf: {
      title: 'Widerrufsbelehrung',
      sections: [
        {
          title: 'Widerrufsrecht',
          content: `Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.

AUSNAHME: Gemäß § 312g Abs. 2 Nr. 2 BGB besteht das Widerrufsrecht NICHT bei Waren, die schnell verderben können oder deren Verfallsdatum schnell überschritten würde.

Da unsere Donuts frische, verderbliche Lebensmittel sind, ist ein Widerruf nach Versand leider nicht möglich.`
        },
        {
          title: 'Stornierung vor Versand',
          content: `Sie können Ihre Bestellung bis zum Tag vor dem geplanten Versand kostenfrei stornieren.

Kontaktieren Sie uns dazu per E-Mail: info@fancy-donuts.com

Bitte geben Sie Ihre Bestellnummer an.`
        },
        {
          title: 'Beschädigte Ware',
          content: `Sollte Ihre Bestellung beschädigt ankommen, gilt unsere Frischegarantie. Kontaktieren Sie uns innerhalb von 24 Stunden mit:
• Fotos der beschädigten Donuts
• Fotos der Verpackung
• Ihrer Bestellnummer

Wir senden Ihnen dann kostenlos Ersatz zu.`
        }
      ]
    }
  };

  // Legal Page Component
  if (legalPage) {
    const content = legalContent[legalPage];
    const pageIcons = {
      impressum: Building2,
      datenschutz: Shield,
      agb: FileText,
      widerruf: Unlock
    };
    const PageIcon = pageIcons[legalPage] || FileText;
    
    return (
      <div className="min-h-screen" style={{ backgroundColor: theme.bg, color: theme.text }}>
        {/* Animated Background */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-10" 
            style={{ background: `radial-gradient(circle, ${BRAND.turquoise}, transparent)` }} />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-10" 
            style={{ background: `radial-gradient(circle, ${BRAND.pink}, transparent)` }} />
        </div>

        {/* Header */}
        <header className="sticky top-0 z-50 py-4 px-6" style={{ backgroundColor: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${theme.border}` }}>
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <button 
              onClick={() => setLegalPage(null)} 
              className="flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-all hover:-translate-x-1"
              style={{ color: BRAND.turquoise }}
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              {language === 'de' ? 'Zurück zur Startseite' : 'Back to Homepage'}
            </button>
            <img 
              src="https://customer-assets.emergentagent.com/job_37cca134-41a5-49ca-9252-6c0a7dd12fc1/artifacts/rsel26pf_Fancy_Donuts_Wortmarke.png" 
              alt="Fancy Donuts" 
              className="h-8 w-auto"
            />
          </div>
        </header>

        {/* Hero Section */}
        <div className="relative py-16 px-6 text-center overflow-hidden">
          <div className="max-w-5xl mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center transform rotate-3 hover:rotate-0 transition-transform"
              style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
              <PageIcon className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-black mb-4" style={{ 
              background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink}, ${BRAND.yellow})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {content.title}
            </h1>
            <p className="text-lg" style={{ color: theme.textMuted }}>
              {language === 'de' ? 'Stand: Februar 2025' : 'Last updated: February 2025'}
            </p>
          </div>
        </div>

        {/* Content */}
        <main className="max-w-5xl mx-auto px-6 pb-16">
          {/* Table of Contents */}
          <div className="mb-12 p-6 rounded-2xl" style={{ backgroundColor: `${BRAND.turquoise}08`, border: `1px solid ${BRAND.turquoise}20` }}>
            <h3 className="font-bold mb-4 flex items-center gap-2" style={{ color: BRAND.turquoise }}>
              <FileText className="w-4 h-4" />
              {language === 'de' ? 'Inhaltsverzeichnis' : 'Table of Contents'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {content.sections.map((section, index) => (
                <button 
                  key={index}
                  onClick={() => document.getElementById(`section-${index}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                  className="text-left text-sm py-2 px-3 rounded-lg transition-all hover:translate-x-1"
                  style={{ color: theme.textMuted }}
                >
                  <span style={{ color: BRAND.pink }}>{String(index + 1).padStart(2, '0')}.</span> {section.title}
                </button>
              ))}
            </div>
          </div>

          {/* Sections with Alternating Design */}
          <div className="space-y-6">
            {content.sections.map((section, index) => (
              <div 
                key={index}
                id={`section-${index}`}
                className="group relative"
              >
                {/* Connector Line */}
                {index < content.sections.length - 1 && (
                  <div className="absolute left-8 top-full w-0.5 h-6 z-10" 
                    style={{ background: `linear-gradient(to bottom, ${BRAND.turquoise}40, transparent)` }} />
                )}
                
                <div 
                  className="relative p-6 md:p-8 rounded-2xl transition-all duration-300 hover:scale-[1.01]"
                  style={{ 
                    backgroundColor: index % 2 === 0 ? theme.bgCard : `${BRAND.turquoise}05`,
                    border: `1px solid ${index % 2 === 0 ? theme.border : `${BRAND.turquoise}15`}`,
                  }}
                >
                  {/* Section Number Badge */}
                  <div className="absolute -top-3 -left-3 w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-black shadow-lg transform group-hover:scale-110 transition-transform"
                    style={{ 
                      background: index % 3 === 0 
                        ? `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` 
                        : index % 3 === 1 
                          ? `linear-gradient(135deg, ${BRAND.pink}, ${BRAND.yellow})`
                          : `linear-gradient(135deg, ${BRAND.yellow}, ${BRAND.turquoise})`
                    }}>
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  
                  <h2 className="text-xl md:text-2xl font-black mb-4 pl-6" style={{ color: theme.text }}>
                    {section.title}
                  </h2>
                  
                  <div className="pl-6">
                    <p className="whitespace-pre-line leading-relaxed text-base" style={{ color: theme.textMuted }}>
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation zwischen Legal Pages */}
          <div className="mt-16 pt-8 border-t" style={{ borderColor: theme.border }}>
            <h3 className="text-center text-sm font-bold mb-6" style={{ color: theme.textMuted }}>
              {language === 'de' ? 'Weitere rechtliche Informationen' : 'More Legal Information'}
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {Object.entries(legalContent).map(([key, value]) => {
                const Icon = pageIcons[key] || FileText;
                return key !== legalPage && (
                  <button
                    key={key}
                    onClick={() => { setLegalPage(key); window.scrollTo(0, 0); }}
                    className="group flex items-center gap-3 px-6 py-3 rounded-xl text-sm font-medium transition-all hover:scale-105"
                    style={{ 
                      backgroundColor: theme.bgCard, 
                      border: `1px solid ${theme.border}` 
                    }}
                  >
                    <Icon className="w-4 h-4 group-hover:rotate-12 transition-transform" style={{ color: BRAND.turquoise }} />
                    <span>{value.title}</span>
                    <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" style={{ color: BRAND.pink }} />
                  </button>
                );
              })}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 px-6 text-center" style={{ borderTop: `1px solid ${theme.border}` }}>
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm" style={{ color: theme.textMuted }}>
              © 2025 Fancy Donuts. {language === 'de' ? 'Alle Rechte vorbehalten.' : 'All rights reserved.'}
            </p>
            <div className="flex items-center gap-4">
              <a href="mailto:info@fancy-donuts.com" className="text-sm flex items-center gap-2 transition-colors hover:opacity-70" style={{ color: BRAND.turquoise }}>
                <Mail className="w-4 h-4" /> info@fancy-donuts.com
              </a>
              <a href="tel:03312345454" className="text-sm flex items-center gap-2 transition-colors hover:opacity-70" style={{ color: BRAND.pink }}>
                <Phone className="w-4 h-4" /> 033 123 54 77 54
              </a>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (currentView === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: theme.bg, color: theme.text }}>
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-black mb-4">Bestellung erfolgreich!</h1>
          <p className="mb-2" style={{ color: theme.textMuted }}>Vielen Dank für deine Bestellung!</p>
          <p className="mb-8 text-sm" style={{ color: theme.textMuted }}>Du erhältst in Kürze eine Bestätigungs-E-Mail mit allen Details.</p>
          
          <div className="space-y-3">
            {/* Zum Dashboard Button - primär */}
            {user ? (
              <button 
                onClick={() => { 
                  setCurrentView('home'); 
                  window.history.pushState({}, '', '/'); 
                  setShowDashboard(true);
                  fetchDashboard();
                }}
                className="w-full px-8 py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2" 
                style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
                data-testid="success-goto-dashboard"
              >
                <User className="w-5 h-5" />
                Zu deinem Dashboard
              </button>
            ) : (
              <button 
                onClick={() => { 
                  setCurrentView('home'); 
                  window.history.pushState({}, '', '/'); 
                  setShowAuth(true);
                }}
                className="w-full px-8 py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2" 
                style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
              >
                <User className="w-5 h-5" />
                Anmelden / Registrieren
              </button>
            )}
            
            {/* Zurück zur Startseite Button - sekundär */}
            <button 
              onClick={() => { setCurrentView('home'); window.history.pushState({}, '', '/'); }}
              className="w-full px-8 py-3 rounded-xl font-medium" 
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}
            >
              Zurück zur Startseite
            </button>
          </div>
          
          {/* Info-Box */}
          <div className="mt-8 p-4 rounded-xl text-left text-sm" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <p className="font-semibold mb-2" style={{ color: BRAND.turquoise }}>Was passiert jetzt?</p>
            <ul className="space-y-1" style={{ color: theme.textMuted }}>
              <li>📧 Bestätigungs-E-Mail mit Rechnung</li>
              <li>📦 Bearbeitung innerhalb von 24h</li>
              <li>🚚 Versand mit Tracking-Info</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-200" style={{ backgroundColor: theme.bg, color: theme.text }}>
      <Toaster position="top-center" richColors />

      {/* KONFETTI ANIMATION - Premium Feuerwerk wenn alle Donuts ausgewählt */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
          {/* Große glitzernde Partikel */}
          {[...Array(30)].map((_, i) => (
            <div
              key={`star-${i}`}
              className="absolute animate-ping"
              style={{
                left: `${10 + Math.random() * 80}%`,
                top: `${10 + Math.random() * 60}%`,
                width: '8px',
                height: '8px',
                backgroundColor: [BRAND.turquoise, BRAND.pink, BRAND.yellow][Math.floor(Math.random() * 3)],
                borderRadius: '50%',
                boxShadow: `0 0 20px ${[BRAND.turquoise, BRAND.pink, BRAND.yellow][Math.floor(Math.random() * 3)]}`,
                animation: `confetti-burst ${0.8 + Math.random() * 0.5}s ease-out forwards`,
                animationDelay: `${Math.random() * 0.3}s`,
              }}
            />
          ))}
          {/* Fallende Konfetti-Streifen */}
          {[...Array(40)].map((_, i) => (
            <div
              key={`strip-${i}`}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-30px',
                width: `${4 + Math.random() * 4}px`,
                height: `${15 + Math.random() * 20}px`,
                background: `linear-gradient(180deg, ${[BRAND.turquoise, BRAND.pink, BRAND.yellow, '#9b5de5', '#22c55e', '#ff6b6b'][Math.floor(Math.random() * 6)]}, transparent)`,
                borderRadius: '2px',
                animation: `confetti-fall-spin ${2.5 + Math.random() * 1.5}s ease-out forwards`,
                animationDelay: `${Math.random() * 0.8}s`,
              }}
            />
          ))}
          {/* Emoji-Donuts die fallen */}
          {[...Array(8)].map((_, i) => (
            <div
              key={`donut-${i}`}
              className="absolute text-3xl"
              style={{
                left: `${10 + Math.random() * 80}%`,
                top: '-50px',
                animation: `confetti-fall-spin ${3 + Math.random() * 1}s ease-out forwards`,
                animationDelay: `${0.2 + Math.random() * 0.5}s`,
              }}
            >
              🍩
            </div>
          ))}
        </div>
      )}

      {/* SCROLLING BANNER - Refactored Component */}
      <AnnouncementBanner />

      {/* HEADER - Refactored Component */}
      <Header 
        scrollY={scrollY}
        theme={theme}
        language={language}
        setLanguage={setLanguage}
        user={user}
        setShowAuth={setShowAuth}
        setShowDashboard={setShowDashboard}
        fetchDashboard={fetchDashboard}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        t={t}
      />
      
      {/* SLIDE-OUT MENU - Refactored Component */}
      <SlideOutMenu 
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        theme={theme}
        scrollToConfigurator={scrollToConfigurator}
        setLegalPage={setLegalPage}
      />

      {/* HERO - Refactored Component */}
      <HeroSection 
        scrollToConfigurator={scrollToConfigurator}
        configuratorRef={configuratorRef}
      />

      {/* SO FUNKTIONIERT'S - Refactored Component */}
      <HowItWorksSection theme={theme} t={t} scrollY={scrollY} />

      {/* KONFIGURATOR - Mobile-optimiert */}
      <section ref={configuratorRef} className="py-12 sm:py-24" style={{ backgroundColor: theme.bg }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          {/* ABO vs CATERING Toggle - größere Touch-Targets */}
          <div className="mb-6 sm:mb-8">
            <div className="flex justify-center">
              <div className="inline-flex p-1 sm:p-1.5 rounded-2xl w-full sm:w-auto max-w-md" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                <button 
                  onClick={() => setOrderMode('abo')}
                  className="flex-1 sm:flex-none px-4 sm:px-8 py-3.5 sm:py-3 rounded-xl font-bold text-sm transition-all"
                  style={{ 
                    background: orderMode === 'abo' ? `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` : 'transparent',
                    color: orderMode === 'abo' ? 'white' : theme.textMuted
                  }}
                  data-testid="tab-abo"
                >
                  {t.configurator.aboTab}
                </button>
                <button 
                  onClick={() => setOrderMode('catering')}
                  className="flex-1 sm:flex-none px-4 sm:px-8 py-3.5 sm:py-3 rounded-xl font-bold text-sm transition-all"
                  style={{ 
                    background: orderMode === 'catering' ? `linear-gradient(135deg, ${BRAND.pink}, ${BRAND.yellow})` : 'transparent',
                    color: orderMode === 'catering' ? 'white' : theme.textMuted
                  }}
                  data-testid="tab-catering"
                >
                  {t.configurator.cateringTab}
                </button>
              </div>
            </div>
            <p className="text-center text-xs sm:text-sm mt-3" style={{ color: theme.textMuted }}>
              {orderMode === 'abo' ? t.configurator.aboDesc : t.configurator.cateringDesc}
            </p>
          </div>

          {/* Schritt 1 + 2 MITTIG */}
          <div className="space-y-4 sm:space-y-6">
            {/* Step 1: Teamgröße - Mobile-optimiert mit größeren Buttons */}
            <div className="rounded-2xl p-4 sm:p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                  <Users className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                </div>
                <div>
                  <span className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>{language === 'de' ? 'Schritt' : 'Step'} 1</span>
                  <h3 className="font-bold text-sm sm:text-base">{orderMode === 'abo' ? t.configurator.step1Title : t.configurator.step1TitleCatering}</h3>
                </div>
              </div>
              
              {/* Schnellwahl-Buttons - 4 in einer Reihe auf Mobile */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {TEAM_SIZES.map(size => (
                  <button key={size} onClick={() => { 
                    if (size === '100+') {
                      setExtendedSlider(true);
                      setTeamSize(150);
                      setBoxCount(Math.ceil(150 / DONUTS_PER_BOX));
                    } else {
                      setExtendedSlider(false);
                      setTeamSize(size); 
                      setBoxCount(Math.ceil(size / DONUTS_PER_BOX)); 
                    }
                    setSelectedDonuts({}); 
                    // Glow-Animation auslösen - zeigt berechnete Boxen für 2 Sekunden
                    setBoxGlowing(true);
                    setTimeout(() => setBoxGlowing(false), 2500);
                    // Auto-scroll zu Schritt 2 - nach 2 Sekunden damit User Box-Berechnung sieht
                    setTimeout(() => {
                      const step2 = document.getElementById('step-2-selection');
                      if (step2) {
                        const headerOffset = 100;
                        const elementPosition = step2.getBoundingClientRect().top;
                        const targetPosition = elementPosition + window.pageYOffset - headerOffset;
                        
                        // Smooth scroll zu Schritt 2
                        window.scrollTo({
                          top: targetPosition,
                          behavior: 'smooth'
                        });
                      }
                    }, 2000); // 2 Sekunden warten damit User Box-Berechnung sehen kann
                  }}
                    className="px-2 sm:px-4 py-2.5 sm:py-2 rounded-xl font-bold text-sm transition-all active:scale-95"
                    style={{ 
                      background: teamSize === size || (size === '100+' && extendedSlider) ? `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` : 'rgba(255,255,255,0.05)', 
                      color: teamSize === size || (size === '100+' && extendedSlider) ? 'white' : theme.text 
                    }}
                    data-testid={`team-size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              
              {/* Range Slider - größerer Touch-Bereich */}
              <div className="mb-4">
                <label className="text-xs sm:text-sm block mb-2" style={{ color: theme.textMuted }}>
                  {t.configurator.individualSize}: <span className="font-bold" style={{ color: BRAND.turquoise }}>{teamSize}</span> {orderMode === 'abo' ? t.configurator.persons : t.configurator.guests}
                  {extendedSlider && <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${BRAND.pink}20`, color: BRAND.pink }}>{language === 'de' ? 'Erweiterte Spanne' : 'Extended range'}</span>}
                </label>
                <input 
                  type="range" 
                  min={extendedSlider ? "100" : "1"} 
                  max={extendedSlider ? "500" : "100"} 
                  value={teamSize} 
                  onChange={(e) => { 
                    const newSize = Number(e.target.value);
                    setTeamSize(newSize); 
                    setBoxCount(Math.ceil(newSize / DONUTS_PER_BOX)); 
                    setSelectedDonuts({}); 
                    // Glow-Animation auslösen - längere Duration für smoother Effekt
                    setBoxGlowing(true);
                    setTimeout(() => setBoxGlowing(false), 900);
                  }}
                  className="w-full h-3 sm:h-2 rounded-lg appearance-none cursor-pointer touch-pan-y"
                  style={{ 
                    background: extendedSlider 
                      ? `linear-gradient(to right, ${BRAND.turquoise} 0%, ${BRAND.pink} ${((teamSize - 100) / 400) * 100}%, rgba(255,255,255,0.1) ${((teamSize - 100) / 400) * 100}%, rgba(255,255,255,0.1) 100%)`
                      : `linear-gradient(to right, ${BRAND.turquoise} 0%, ${BRAND.pink} ${(teamSize / 100) * 100}%, rgba(255,255,255,0.1) ${(teamSize / 100) * 100}%, rgba(255,255,255,0.1) 100%)`,
                  }}
                />
                <div className="flex justify-between text-xs mt-1" style={{ color: theme.textMuted }}>
                  {extendedSlider ? (
                    <>
                      <span>100</span>
                      <span>200</span>
                      <span>300</span>
                      <span>400</span>
                      <span>500</span>
                    </>
                  ) : (
                    <>
                      <span>1</span>
                      <span>25</span>
                      <span>50</span>
                      <span>75</span>
                      <span>100</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Box Info - mit smoothem Glow-Puls bei Neuberechnung */}
              <div 
                className={`p-4 rounded-xl flex items-center justify-between transition-all duration-200 ease-out ${boxGlowing ? 'box-glow-pulse' : ''}`}
                style={{ 
                  backgroundColor: `${BRAND.turquoise}10`,
                  boxShadow: boxGlowing ? `0 0 20px ${BRAND.turquoise}40, 0 0 40px ${BRAND.turquoise}20, inset 0 0 20px ${BRAND.turquoise}10` : 'none',
                  transform: boxGlowing ? 'scale(1.01)' : 'scale(1)',
                  border: `2px solid ${boxGlowing ? BRAND.turquoise + '60' : 'transparent'}`
                }}
              >
                <span className="text-sm" style={{ color: theme.textMuted }}>{language === 'de' ? 'Berechnete Boxen:' : 'Calculated boxes:'}</span>
                <span className={`text-lg font-black transition-all duration-300 ${boxGlowing ? 'scale-110' : 'scale-100'}`} style={{ color: BRAND.turquoise }}>{boxCount} {language === 'de' ? 'Boxen' : 'Boxes'} = {maxDonuts} Donuts</span>
              </div>
            </div>

            {/* Step 2: Auswahl-Methode - MYSTERY BOX oder SELBST WÄHLEN */}
            <div id="step-2-selection" className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}15` }}>
                  <Package className="w-5 h-5" style={{ color: BRAND.pink }} />
                </div>
                <div>
                  <span className="text-sm" style={{ color: theme.textMuted }}>{language === 'de' ? 'Schritt' : 'Step'} 2</span>
                  <h3 className="font-bold">{t.configurator.step2Title}</h3>
                </div>
                </div>
                
                {/* Zwei Optionen */}
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {/* Option 1: Selbst wählen */}
                  <button onClick={() => { 
                    setAutoMix(false); 
                    setSelectedDonuts({}); 
                    // Auto-scroll zu "Wähle deine Donuts" nach kurzer Verzögerung
                    setTimeout(() => {
                      const donutsSection = document.getElementById('donuts-section');
                      if (donutsSection) {
                        const headerOffset = 80;
                        const elementPosition = donutsSection.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                      }
                    }, 100);
                  }}
                    className="p-6 rounded-2xl text-left transition-all hover:scale-[1.02]"
                    style={{ 
                      backgroundColor: !autoMix ? `${BRAND.turquoise}10` : 'transparent', 
                      border: `2px solid ${!autoMix ? BRAND.turquoise : theme.border}`,
                      boxShadow: !autoMix ? `0 0 20px ${BRAND.turquoise}30` : 'none'
                    }}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${BRAND.turquoise}20` }}>
                      <Package className="w-6 h-6" style={{ color: BRAND.turquoise }} />
                    </div>
                    <h4 className="font-bold text-lg mb-1">Selbst zusammenstellen</h4>
                    <p className="text-sm" style={{ color: theme.textMuted }}>Wähle jeden Donut einzeln aus unserem Sortiment von 37 Sorten.</p>
                    {!autoMix && <div className="mt-3 text-xs font-bold" style={{ color: BRAND.turquoise }}>✓ Ausgewählt</div>}
                  </button>

                  {/* Option 2: Mystery Box */}
                  <button onClick={() => { 
                    setAutoMix(true); 
                    setSelectedDonuts({}); 
                    // Auto-scroll zu Mystery Box Preiskategorien - langsamer und smoother
                    setTimeout(() => {
                      const mysteryPrices = document.getElementById('mystery-box-prices');
                      if (mysteryPrices) {
                        const headerOffset = 80;
                        const elementPosition = mysteryPrices.getBoundingClientRect().top;
                        const targetPosition = elementPosition + window.pageYOffset - headerOffset;
                        const startPosition = window.pageYOffset;
                        const distance = targetPosition - startPosition;
                        const duration = 1200; // Längere Duration für smootheren Scroll
                        let start = null;
                        
                        const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
                        
                        const animation = (currentTime) => {
                          if (start === null) start = currentTime;
                          const timeElapsed = currentTime - start;
                          const progress = Math.min(timeElapsed / duration, 1);
                          const ease = easeInOutCubic(progress);
                          window.scrollTo(0, startPosition + distance * ease);
                          if (timeElapsed < duration) requestAnimationFrame(animation);
                        };
                        requestAnimationFrame(animation);
                      }
                    }, 400); // Mehr Verzögerung
                  }}
                    className="p-6 rounded-2xl text-left transition-all hover:scale-[1.02]"
                    style={{ 
                      backgroundColor: autoMix ? `${BRAND.pink}10` : 'transparent', 
                      border: `2px solid ${autoMix ? BRAND.pink : theme.border}`,
                      boxShadow: autoMix ? `0 0 20px ${BRAND.pink}30` : 'none'
                    }}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${BRAND.pink}20` }}>
                      <Gift className="w-6 h-6" style={{ color: BRAND.pink }} />
                    </div>
                    <h4 className="font-bold text-lg mb-1">🎁 Mystery Box</h4>
                    <p className="text-sm" style={{ color: theme.textMuted }}>Lass dich überraschen! Wir stellen die perfekte Box für dich zusammen.</p>
                    {autoMix && <div className="mt-3 text-xs font-bold" style={{ color: BRAND.pink }}>✓ Ausgewählt</div>}
                  </button>
                </div>

                {/* Mystery Box - Preiskategorie Auswahl */}
                {autoMix && (
                  <div id="mystery-box-prices" className="p-5 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                    <p className="font-bold mb-4">Wähle deine Mystery Box:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { id: 'standard', name: 'Classics', price: 3, desc: 'Klassiker', color: BRAND.turquoise },
                        { id: 'premium', name: 'Specials', price: 4, desc: 'Beliebte Sorten', color: BRAND.yellow },
                        { id: 'deluxe', name: 'Dossants', price: 5, desc: 'Premium Dossants', color: BRAND.pink },
                        { id: 'mix', name: 'Mix', price: 4, desc: 'Von allem etwas', color: '#9b5de5' },
                      ].map(cat => (
                        <button key={cat.id} onClick={() => {
                          setPriceCategory(cat.id);
                          // Auto-scroll zu Schritt 3 - SEHR LANGSAM und ENTSPANNT
                          // 3 Sekunden warten, dann 2.5 Sekunden scrollen
                          setTimeout(() => {
                            if (intervalRef.current) {
                              const element = intervalRef.current;
                              const headerOffset = 100;
                              const elementPosition = element.getBoundingClientRect().top;
                              const targetPosition = elementPosition + window.pageYOffset - headerOffset;
                              const startPosition = window.pageYOffset;
                              const distance = targetPosition - startPosition;
                              const duration = 2500; // 2.5 Sekunden für sanften Scroll
                              let start = null;
                              
                              const easeOutExpo = (t) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
                              
                              const animation = (currentTime) => {
                                if (start === null) start = currentTime;
                                const timeElapsed = currentTime - start;
                                const progress = Math.min(timeElapsed / duration, 1);
                                const ease = easeOutExpo(progress);
                                window.scrollTo(0, startPosition + distance * ease);
                                if (timeElapsed < duration) requestAnimationFrame(animation);
                              };
                              requestAnimationFrame(animation);
                            }
                          }, 3000); // 3 Sekunden warten vor dem Scroll
                        }}
                          className="p-4 rounded-xl text-center transition-all hover:scale-105"
                          style={{ 
                            backgroundColor: priceCategory === cat.id ? `${cat.color}20` : 'transparent', 
                            border: `2px solid ${priceCategory === cat.id ? cat.color : theme.border}`,
                            boxShadow: priceCategory === cat.id ? `0 0 15px ${cat.color}30` : 'none'
                          }}>
                          <div className="text-2xl font-black" style={{ color: cat.color }}>{cat.price}€</div>
                          <div className="font-bold text-sm">{cat.name}</div>
                          <div className="text-xs mt-1" style={{ color: theme.textMuted }}>{cat.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selbst wählen - Hinweis */}
                {!autoMix && (
                  <div className="p-4 rounded-xl" style={{ backgroundColor: `${BRAND.turquoise}10`, border: `1px solid ${BRAND.turquoise}30` }}>
                    <p className="font-bold" style={{ color: BRAND.turquoise }}>
                      ✓ Selbst zusammenstellen ausgewählt
                    </p>
                    <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
                      Wähle unten deine {maxDonuts} Donuts aus · Bereits ausgewählt: <span className="font-bold" style={{ color: selectedDonutCount >= maxDonuts ? BRAND.turquoise : BRAND.pink }}>{selectedDonutCount} / {maxDonuts}</span>
                    </p>
                  </div>
                )}
              </div>
          </div>
        </div>
      </section>

      {/* GROSSE DONUT SHOP SECTION - NACH SCHRITT 2 */}
      {!autoMix && (
        <section id="donuts-section" className="py-16" style={{ backgroundColor: darkMode ? '#0d0d0d' : '#f8fafc' }}>
          <div className="max-w-7xl mx-auto px-6">
            {/* Header */}
            <div className="text-center mb-12">
              <span className="inline-block mb-4 px-5 py-2 rounded-full text-sm font-semibold" 
                style={{ background: `${BRAND.pink}15`, border: `1px solid ${BRAND.pink}30`, color: BRAND.pink }}>
                {t.donutShop.yourSelection}
              </span>
              <h2 className="text-4xl md:text-5xl font-black mb-4">
                {t.donutShop.title} <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`, WebkitBackgroundClip: 'text' }}>{t.donutShop.titleHighlight}</span>
              </h2>
              <p className="text-lg max-w-xl mx-auto" style={{ color: theme.textMuted }}>
                {t.donutShop.subtitle} {maxDonuts} {t.donutShop.donutsForTeam}
              </p>
            </div>

            {/* Sticky Filter Bar - mit Abstand zum Header und nach unten */}
            <div className="sticky top-14 z-40 mb-6 sm:mb-10 -mx-4 sm:mx-0 px-2 sm:px-0 pt-4">
              <div className="p-2 sm:p-3 rounded-none sm:rounded-2xl backdrop-blur-xl" 
                style={{ 
                  backgroundColor: 'rgba(10,10,10,0.98)', 
                  borderTop: '1px solid rgba(255,255,255,0.15)',
                  borderBottom: '1px solid rgba(255,255,255,0.15)',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                }}>
                {/* 5 Kategorien - Alle passen auf ein Handy */}
                <div className="w-full">
                  <div className="flex justify-center gap-1.5 sm:gap-3 p-2 sm:p-3 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {donutCategories.map((cat, index) => {
                      const isActive = activeDonutCategory === cat;
                      const categoryStyles = {
                        'Alle': { bg: '#374151', border: '#4B5563' },
                        'Classics': { bg: '#78350F', border: '#92400E' },
                        'Specials': { bg: '#831843', border: '#9D174D' },
                        'Premium': { bg: '#4C1D95', border: '#5B21B6' },
                        'Boxen': { bg: '#064E3B', border: '#047857' }
                      };
                      const style = categoryStyles[cat] || categoryStyles['Alle'];
                      
                      return (
                      <button key={cat} onClick={() => {
                        setActiveDonutCategory(cat);
                        setVeganFilter(false);
                      }}
                        className="px-2 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl text-[10px] sm:text-sm font-semibold transition-all whitespace-nowrap"
                        style={isActive 
                          ? { 
                              background: style.bg, 
                              color: 'white',
                              border: `1px solid ${style.border}`,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                            }
                          : { 
                              background: 'rgba(255,255,255,0.05)',
                              color: 'rgba(255,255,255,0.6)',
                              border: '1px solid rgba(255,255,255,0.1)'
                            }}
                        data-testid={`category-filter-${cat}`}
                      >
                        {cat === 'Alle' ? t.donutShop.filterAll : cat}
                      </button>
                    )})}
                  </div>
                </div>
              </div>
            </div>

            {/* SHOP LAYOUT - Mobile-optimierte Karten */}
            <div className="w-full">
              {/* Donut Karten - 2 Spalten auf Mobile, 3 auf Desktop */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {filteredDonuts.map(donut => (
                <div key={donut.id} 
                  className="group rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-200 hover:scale-[1.02] donut-card-large flex flex-col" 
                  data-testid={`donut-card-${donut.id}`}
                  style={{ 
                    backgroundColor: darkMode ? 'rgba(20,20,25,0.95)' : '#ffffff', 
                    border: `2px solid ${selectedDonuts[donut.id] > 0 ? BRAND.turquoise : 'transparent'}`,
                    boxShadow: selectedDonuts[donut.id] > 0 
                      ? `0 0 20px ${BRAND.turquoise}50, 0 10px 25px rgba(0,0,0,0.3)` 
                      : '0 8px 20px rgba(0,0,0,0.15)'
                  }}>
                  
                  {/* Image Section - 10% GRÖSSERE Bilder mit coolem Hintergrund */}
                  <div className="relative h-64 sm:h-80 md:h-96 flex-shrink-0 flex items-center justify-center overflow-hidden" 
                    style={{ 
                      background: donut.category === 'Dossants' || donut.category === 'Berliner'
                        ? `radial-gradient(circle at 50% 50%, ${BRAND.pink}35 0%, ${BRAND.pink}15 40%, transparent 70%), linear-gradient(180deg, rgba(243,157,178,0.2) 0%, rgba(255,209,111,0.1) 50%, transparent 100%)`
                        : donut.category === 'Specials'
                          ? `radial-gradient(circle at 50% 50%, ${BRAND.yellow}35 0%, ${BRAND.yellow}15 40%, transparent 70%), linear-gradient(180deg, rgba(255,209,111,0.2) 0%, rgba(243,157,178,0.1) 50%, transparent 100%)`
                          : `radial-gradient(circle at 50% 50%, ${BRAND.turquoise}35 0%, ${BRAND.turquoise}15 40%, transparent 70%), linear-gradient(180deg, rgba(129,203,214,0.2) 0%, rgba(243,157,178,0.1) 50%, transparent 100%)`
                    }}>
                    
                    {/* Decorative Background - Animated Glow */}
                    <div className="absolute inset-0 overflow-hidden">
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 sm:w-56 sm:h-56 rounded-full opacity-40 blur-3xl animate-pulse"
                        style={{ backgroundColor: donut.category === 'Dossants' || donut.category === 'Berliner' ? BRAND.pink : donut.category === 'Specials' ? BRAND.yellow : BRAND.turquoise }}></div>
                      <div className="absolute -top-8 -right-8 w-32 h-32 sm:w-44 sm:h-44 rounded-full opacity-25 blur-2xl"
                        style={{ backgroundColor: donut.category === 'Dossants' || donut.category === 'Berliner' ? BRAND.yellow : donut.category === 'Specials' ? BRAND.pink : BRAND.yellow }}></div>
                      <div className="absolute -bottom-8 -left-8 w-28 h-28 sm:w-36 sm:h-36 rounded-full opacity-20 blur-2xl"
                        style={{ backgroundColor: donut.category === 'Dossants' || donut.category === 'Berliner' ? BRAND.turquoise : donut.category === 'Specials' ? BRAND.turquoise : BRAND.pink }}></div>
                    </div>
                    
                    {/* Donut Image - 10% GRÖSSER */}
                    <img 
                      src={donut.image} 
                      alt={donut.name} 
                      className="h-56 w-56 sm:h-68 sm:w-68 md:h-80 md:w-80 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 relative z-10" 
                      style={{ filter: 'drop-shadow(0 20px 35px rgba(0,0,0,0.4))' }}
                    />
                    
                    {/* Category + Vegan Badge - Mobile-kompakt */}
                    <div className="absolute top-2 sm:top-4 left-2 sm:left-4 right-2 sm:right-4 flex justify-between items-start z-20">
                      {/* Vegan Badge links */}
                      {donut.vegan && (
                        <span className="px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-bold flex items-center gap-1"
                          style={{ 
                            background: 'linear-gradient(135deg, #22c55e, #10b981)',
                            color: 'white',
                            boxShadow: '0 2px 10px rgba(34,197,94,0.4)'
                          }}>
                          <Leaf className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> <span className="hidden sm:inline">Vegan</span>
                        </span>
                      )}
                      {!donut.vegan && <div></div>}
                      
                      {/* Category Badge rechts - Mobile-kompakt */}
                      <span className="px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-bold text-white"
                        style={{ 
                          background: donut.category === 'Dossants' 
                            ? `linear-gradient(135deg, ${BRAND.pink}, #e879a0)` 
                            : donut.category === 'Berliner'
                              ? `linear-gradient(135deg, #9b5de5, #7c3aed)`
                            : donut.category === 'Specials' 
                              ? `linear-gradient(135deg, ${BRAND.yellow}, #e6b84d)` 
                              : `linear-gradient(135deg, ${BRAND.turquoise}, #5cb3c0)`,
                          boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                        }}>
                        {donut.category}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content Section - Mobile-kompakt */}
                  <div className="p-3 sm:p-5 flex flex-col flex-grow">
                    {/* Name und Preis in einer Zeile */}
                    <div className="flex items-start justify-between mb-1 sm:mb-2">
                      <h4 className="font-bold text-sm sm:text-lg md:text-xl leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>{donut.name}</h4>
                      <span className="text-xs sm:text-lg font-bold ml-1 flex-shrink-0" style={{ color: theme.textMuted }}>{donut.price.toFixed(2)}€</span>
                    </div>
                    
                    <p className="text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2 hidden sm:block" style={{ color: theme.textMuted }}>{donut.description}</p>
                    
                    {/* Details - Nur auf Desktop */}
                    <div className="space-y-1 mb-3 sm:mb-4 text-xs hidden md:block" style={{ color: theme.textMuted }}>
                      <div className="flex"><span className="font-semibold w-14" style={{ color: theme.text }}>{t.donutShop.basis}:</span> <span className="truncate">{donut.basis}</span></div>
                      <div className="flex"><span className="font-semibold w-14" style={{ color: theme.text }}>{t.donutShop.glaze}:</span> <span className="truncate">{donut.glasur}</span></div>
                      <div className="flex"><span className="font-semibold w-14" style={{ color: theme.text }}>{t.donutShop.topping}:</span> <span className="truncate">{donut.topping || '-'}</span></div>
                    </div>
                    
                    {/* Actions - Mobile-optimiert */}
                    <div className="flex items-center justify-center pt-2 sm:pt-4 mt-auto" style={{ borderTop: `1px solid ${theme.border}` }}>
                      {selectedDonuts[donut.id] > 0 ? (
                        <div className="flex items-center gap-2 sm:gap-4">
                          <button onClick={() => removeDonut(donut.id)} 
                            className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center transition-all hover:scale-110 shadow-lg" 
                            style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                            <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                          <span className="w-6 sm:w-10 text-center text-lg sm:text-2xl font-black" style={{ color: BRAND.turquoise }}>{selectedDonuts[donut.id]}</span>
                          <button onClick={() => addDonut(donut.id)} 
                            className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-white transition-all hover:scale-110 shadow-lg" 
                            style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => addDonut(donut.id)} 
                          className="w-full px-3 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold text-white transition-all hover:scale-[1.02] hover:shadow-xl"
                          style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`, boxShadow: `0 8px 25px ${BRAND.turquoise}30` }}>
                          + <span className="hidden sm:inline">Hinzufügen</span><span className="sm:hidden">Add</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>

              {/* Bottom CTA wenn genug ausgewählt */}
              {selectedDonutCount >= maxDonuts && (
                <div className="mt-8 text-center">
                  <div className="inline-block p-5 rounded-2xl" style={{ backgroundColor: `${BRAND.turquoise}10`, border: `2px solid ${BRAND.turquoise}` }}>
                    <p className="font-bold" style={{ color: BRAND.turquoise }}>
                      ✓ Perfekt! Alle {maxDonuts} Donuts ausgewählt.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* KONFIGURATOR FORTSETZUNG - SCHRITTE 3, 4, 5 */}
      <section className="py-24" style={{ backgroundColor: theme.bg }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left: Steps 3, 4, 5 */}
            <div className="lg:col-span-2 space-y-6">

              {/* CATERING: Datum & Lieferart */}
              {orderMode === 'catering' && (
                <>
                  {/* Event Name */}
                  <div className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                        <FileText className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                      </div>
                      <div>
                        <span className="text-sm" style={{ color: theme.textMuted }}>Schritt 3</span>
                        <h3 className="font-bold">Wie heißt dein Event?</h3>
                      </div>
                    </div>
                    <input 
                      type="text" 
                      value={cateringEventName} 
                      onChange={(e) => setCateringEventName(e.target.value)}
                      placeholder="z.B. Firmenjubiläum, Sommerfest, Geburtstag..."
                      className="w-full p-4 rounded-xl outline-none text-lg"
                      style={{ 
                        backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', 
                        border: `1px solid ${cateringEventName ? BRAND.turquoise : theme.border}`,
                        color: theme.text
                      }}
                    />
                  </div>

                  {/* Catering Datum - Verbessert */}
                  <div className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                        <Calendar className="w-5 h-5" style={{ color: BRAND.yellow }} />
                      </div>
                      <div>
                        <span className="text-sm" style={{ color: theme.textMuted }}>Schritt 4</span>
                        <h3 className="font-bold">Wann ist dein Event?</h3>
                      </div>
                    </div>
                    <div className="relative">
                      <input 
                        type="date" 
                        value={cateringDate} 
                        onChange={(e) => setCateringDate(e.target.value)}
                        min={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                        className="w-full p-4 rounded-xl outline-none text-lg font-medium cursor-pointer catering-date-input"
                        style={{ 
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', 
                          border: `2px solid ${cateringDate ? BRAND.yellow : theme.border}`,
                          color: theme.text,
                          colorScheme: darkMode ? 'dark' : 'light'
                        }}
                      />
                      {cateringDate && (
                        <div className="mt-3 p-3 rounded-xl flex items-center gap-3" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                          <span className="text-2xl">📅</span>
                          <div>
                            <p className="font-bold" style={{ color: BRAND.yellow }}>
                              {new Date(cateringDate).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs mt-3" style={{ color: theme.textMuted }}>⏰ Mindestens 48 Stunden Vorlaufzeit erforderlich</p>
                  </div>

                  {/* Catering Lieferart */}
                  <div className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}15` }}>
                        <MapPin className="w-5 h-5" style={{ color: BRAND.pink }} />
                      </div>
                      <div>
                        <span className="text-sm" style={{ color: theme.textMuted }}>Schritt 5</span>
                        <h3 className="font-bold">Abholung oder Lieferung?</h3>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {/* Abholung */}
                      <button onClick={() => setCateringDelivery('pickup')}
                        className="p-5 rounded-2xl text-left transition-all"
                        style={{ 
                          backgroundColor: cateringDelivery === 'pickup' ? `${BRAND.turquoise}10` : 'transparent', 
                          border: `2px solid ${cateringDelivery === 'pickup' ? BRAND.turquoise : theme.border}`,
                          boxShadow: cateringDelivery === 'pickup' ? `0 0 20px ${BRAND.turquoise}30` : 'none'
                        }}>
                        <div className="text-2xl mb-2">🏪</div>
                        <h4 className="font-bold mb-1">Selbstabholung</h4>
                        <p className="text-sm" style={{ color: theme.textMuted }}>Abholung in Potsdam</p>
                        <div className="mt-3 text-lg font-black" style={{ color: BRAND.turquoise }}>Kostenlos</div>
                      </button>
                      {/* Lieferung */}
                      <button onClick={() => setCateringDelivery('delivery')}
                        className="p-5 rounded-2xl text-left transition-all"
                        style={{ 
                          backgroundColor: cateringDelivery === 'delivery' ? `${BRAND.pink}10` : 'transparent', 
                          border: `2px solid ${cateringDelivery === 'delivery' ? BRAND.pink : theme.border}`,
                          boxShadow: cateringDelivery === 'delivery' ? `0 0 20px ${BRAND.pink}30` : 'none'
                        }}>
                        <div className="text-2xl mb-2">🚚</div>
                        <h4 className="font-bold mb-1">Lieferung</h4>
                        <p className="text-sm" style={{ color: theme.textMuted }}>Berlin & Brandenburg</p>
                        <div className="mt-3 text-lg font-black" style={{ color: BRAND.pink }}>ab 25€</div>
                      </button>
                    </div>
                    {cateringDelivery === 'pickup' && (
                      <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: `${BRAND.turquoise}10` }}>
                        <p className="text-sm font-medium" style={{ color: BRAND.turquoise }}>
                          📍 Abholadresse: Fancy Donuts, Brandenburger Str. 12, 14467 Potsdam
                        </p>
                      </div>
                    )}
                    {cateringDelivery === 'delivery' && (
                      <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: `${BRAND.pink}10` }}>
                        <p className="text-sm" style={{ color: theme.textMuted }}>
                          Lieferung nur im Raum Berlin-Brandenburg. Kosten je nach Entfernung von Potsdam (25€ - 50€).
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Catering Buchungsart */}
                  <div className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                        <Check className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                      </div>
                      <div>
                        <span className="text-sm" style={{ color: theme.textMuted }}>Schritt 6</span>
                        <h3 className="font-bold">Wie möchtest du fortfahren?</h3>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {/* Anfrage */}
                      <button onClick={() => setCateringBookingType('inquiry')}
                        className="p-5 rounded-2xl text-left transition-all"
                        style={{ 
                          backgroundColor: cateringBookingType === 'inquiry' ? `${BRAND.turquoise}10` : 'transparent', 
                          border: `2px solid ${cateringBookingType === 'inquiry' ? BRAND.turquoise : theme.border}`,
                          boxShadow: cateringBookingType === 'inquiry' ? `0 0 20px ${BRAND.turquoise}30` : 'none'
                        }}>
                        <div className="text-2xl mb-2">📩</div>
                        <h4 className="font-bold mb-1">Unverbindliche Anfrage</h4>
                        <p className="text-sm" style={{ color: theme.textMuted }}>Wir melden uns mit einem Angebot bei dir</p>
                        {cateringBookingType === 'inquiry' && <div className="mt-3 text-xs font-bold" style={{ color: BRAND.turquoise }}>✓ Ausgewählt</div>}
                      </button>
                      {/* Fest buchen */}
                      <button onClick={() => setCateringBookingType('booking')}
                        className="p-5 rounded-2xl text-left transition-all"
                        style={{ 
                          backgroundColor: cateringBookingType === 'booking' ? `${BRAND.pink}10` : 'transparent', 
                          border: `2px solid ${cateringBookingType === 'booking' ? BRAND.pink : theme.border}`,
                          boxShadow: cateringBookingType === 'booking' ? `0 0 20px ${BRAND.pink}30` : 'none'
                        }}>
                        <div className="text-2xl mb-2">✅</div>
                        <h4 className="font-bold mb-1">Direkt buchen</h4>
                        <p className="text-sm" style={{ color: theme.textMuted }}>Verbindliche Buchung mit Anzahlung</p>
                        {cateringBookingType === 'booking' && <div className="mt-3 text-xs font-bold" style={{ color: BRAND.pink }}>✓ Ausgewählt</div>}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ABO: Lieferintervall */}
              {orderMode === 'abo' && (
                <div ref={intervalRef} id="step-3-interval" className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                      <Calendar className="w-5 h-5" style={{ color: BRAND.yellow }} />
                    </div>
                    <div>
                      <span className="text-sm" style={{ color: theme.textMuted }}>Schritt 3</span>
                      <h3 className="font-bold">Lieferintervall & Abo</h3>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {INTERVALS.map(int => (
                      <button key={int.id} onClick={() => {
                        setInterval(int.id);
                        // Auto-scroll zu Schritt 4 (Extras)
                        setTimeout(() => {
                          const step4 = document.getElementById('step-4-extras');
                          if (step4) {
                            const headerOffset = 80;
                            const elementPosition = step4.getBoundingClientRect().top;
                            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                          }
                        }, 200);
                      }}
                        className="p-4 rounded-xl text-left transition-all"
                        style={{ backgroundColor: interval === int.id ? `${BRAND.turquoise}10` : 'transparent', border: `1px solid ${interval === int.id ? BRAND.turquoise : theme.border}` }}>
                        <div className="flex items-center justify-between">
                          <span className="font-bold">{int.name}</span>
                          {int.discount > 0 && <span className="px-2 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: `${BRAND.pink}20`, color: BRAND.pink }}>{int.discount}%</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4/5: Extras */}
              <div id="step-4-extras" className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                    <Gift className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                  </div>
                  <div>
                    <span className="text-sm" style={{ color: theme.textMuted }}>{language === 'de' ? 'Schritt' : 'Step'} {orderMode === 'catering' ? '5' : '4'}</span>
                    <h3 className="font-bold">{t.extras.title}</h3>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {EXTRAS.map(extra => (
                    <button key={extra.id} 
                      onClick={() => {
                        if (selectedExtras.includes(extra.id)) {
                          // Open modal to edit
                          setShowExtraModal(extra.id);
                        } else {
                          // Select and open modal for configuration
                          setSelectedExtras(prev => [...prev, extra.id]);
                          setShowExtraModal(extra.id);
                        }
                      }}
                      className="p-4 rounded-xl text-left transition-all"
                      style={{ backgroundColor: selectedExtras.includes(extra.id) ? `${BRAND.turquoise}10` : 'transparent', border: `1px solid ${selectedExtras.includes(extra.id) ? BRAND.turquoise : theme.border}` }}>
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${selectedExtras.includes(extra.id) ? 'text-white' : ''}`}
                          style={{ backgroundColor: selectedExtras.includes(extra.id) ? BRAND.turquoise : 'rgba(255,255,255,0.1)' }}>
                          {selectedExtras.includes(extra.id) && <Check className="w-4 h-4" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-bold text-sm">{extra.name}</span>
                            <span className="text-sm font-bold" style={{ color: BRAND.pink }}>{extra.priceDisplay}</span>
                          </div>
                          <p className="text-xs" style={{ color: theme.textMuted }}>{extra.desc}</p>
                          {/* Show selection info if selected */}
                          {selectedExtras.includes(extra.id) && extra.id === 'coffee' && (
                            <p className="text-xs mt-2 font-bold" style={{ color: BRAND.turquoise }}>✓ {coffeeSize} Pareto (+{coffeeSize === '1kg' ? '35' : '15'}€)</p>
                          )}
                          {selectedExtras.includes(extra.id) && extra.id === 'card' && (
                            <p className="text-xs mt-2 font-bold" style={{ color: BRAND.turquoise }}>✓ {cardMessage ? `"${cardMessage.substring(0, 20)}..."` : 'Karte (+3€)'}</p>
                          )}
                          {selectedExtras.includes(extra.id) && extra.id === 'dips' && (
                            <p className="text-xs mt-2 font-bold" style={{ color: BRAND.turquoise }}>
                              ✓ {Object.values(dipQuantities).reduce((sum, q) => sum + q, 0)} Dips (+{calculateDipPrice().toFixed(2)}€)
                            </p>
                          )}
                          {selectedExtras.includes(extra.id) && extra.id === 'branding' && (
                            <p className="text-xs mt-2 font-bold" style={{ color: BRAND.turquoise }}>✓ {brandingLogo ? 'Logo hochgeladen' : 'Branding'} (+{maxDonuts}€ für {maxDonuts} Donuts)</p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 5/7: Kontakt & Adresse */}
              <div id="step-5-address" className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}15` }}>
                    <Truck className="w-5 h-5" style={{ color: BRAND.pink }} />
                  </div>
                  <div>
                    <span className="text-sm" style={{ color: theme.textMuted }}>{language === 'de' ? 'Schritt' : 'Step'} {orderMode === 'catering' ? '7' : '5'}</span>
                    <h3 className="font-bold">{t.address.title}</h3>
                  </div>
                </div>
                
                {/* KONTO ERFORDERLICH - wenn nicht eingeloggt */}
                {!user && (
                  <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: `${BRAND.pink}15`, border: `1px solid ${BRAND.pink}40` }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND.pink }}>
                        <Lock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-sm" style={{ color: BRAND.pink }}>Konto erforderlich</p>
                        <p className="text-xs" style={{ color: theme.textMuted }}>Für B2B-Bestellungen ist ein Kundenkonto notwendig</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button 
                        onClick={() => { setAuthMode('login'); setShowAuth(true); setShowCheckout(false); }}
                        className="flex-1 py-3 rounded-xl font-bold text-sm"
                        style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
                      >
                        Anmelden
                      </button>
                      <button 
                        onClick={() => { setAuthMode('register'); setShowAuth(true); setShowCheckout(false); }}
                        className="flex-1 py-3 rounded-xl font-bold text-sm text-white"
                        style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
                      >
                        Konto erstellen
                      </button>
                    </div>
                    <p className="text-xs mt-3 text-center" style={{ color: theme.textMuted }}>
                      ✓ Bestellhistorie ✓ Rechnungen downloaden ✓ Schneller nachbestellen
                    </p>
                  </div>
                )}
                
                <div className="space-y-3">
                  <input placeholder={`${t.address.company} *`} value={address.company} onChange={(e) => setAddress({ ...address, company: e.target.value })}
                    className="w-full p-3 rounded-xl outline-none" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
                  
                  {/* Adress-Suche mit Autocomplete */}
                  <div className="relative">
                    <input 
                      placeholder={language === 'de' ? '🔍 Adresse suchen (Straße, Firma, PLZ...)' : '🔍 Search address (street, company, ZIP...)'}
                      onChange={(e) => searchAddress(e.target.value)}
                      className="w-full p-3 rounded-xl outline-none" 
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${BRAND.turquoise}`, color: theme.text }} 
                    />
                    {/* Autocomplete Vorschläge */}
                    {addressSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-2xl" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                        {addressSuggestions.map((suggestion, i) => (
                          <button
                            key={i}
                            onClick={() => selectAddressSuggestion(suggestion)}
                            className="w-full p-3 text-left text-sm hover:bg-white/10 transition-colors flex items-center gap-2"
                            style={{ borderBottom: i < addressSuggestions.length - 1 ? `1px solid ${theme.border}` : 'none' }}
                          >
                            <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: BRAND.pink }} />
                            <span>{suggestion.display}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <input type="email" placeholder={`${t.address.email} *`} value={address.email} onChange={(e) => setAddress({ ...address, email: e.target.value })}
                      className="p-3 rounded-xl outline-none" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
                    <input type="tel" placeholder={`${t.address.phone} *`} value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                      className="p-3 rounded-xl outline-none" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <input placeholder={`${t.address.street} *`} value={address.street} onChange={(e) => setAddress({ ...address, street: e.target.value })}
                      className="col-span-2 p-3 rounded-xl outline-none" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
                    <input placeholder={t.address.number} value={address.number} onChange={(e) => setAddress({ ...address, number: e.target.value })}
                      className="p-3 rounded-xl outline-none" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <input placeholder={`${t.address.zip} *`} value={address.zip} onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                      className="p-3 rounded-xl outline-none" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
                    <input placeholder={`${t.address.city} *`} value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })}
                      className="col-span-2 p-3 rounded-xl outline-none" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
                  </div>
                  
                  {/* Benutzer ist eingeloggt - zeige E-Mail Info */}
                  {user && (
                    <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${theme.border}` }}>
                      <div className="p-4 rounded-xl" style={{ backgroundColor: `${BRAND.turquoise}10`, border: `1px solid ${BRAND.turquoise}30` }}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND.turquoise }}>
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-bold text-sm" style={{ color: BRAND.turquoise }}>Eingeloggt als</p>
                            <p className="text-xs" style={{ color: theme.textMuted }}>{user.email}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Bestellung */}
            <div className="lg:sticky lg:top-24 h-fit">
              <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                <div className="p-4 text-white font-bold" style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                  {t.summary.title}
                </div>
                <div className="p-6 space-y-4">
                  {/* Donuts mit aufklappbarer Detailansicht */}
                  <DonutDetailAccordion 
                    selectedDonuts={selectedDonuts}
                    autoMix={autoMix}
                    maxDonuts={maxDonuts}
                    selectedDonutCount={selectedDonutCount}
                    donutPrice={donutPrice}
                    donuts={donutProducts}
                    t={t}
                    theme={theme}
                    BRAND={BRAND}
                  />
                  {selectedExtras.map(id => {
                    const extra = EXTRAS.find(e => e.id === id);
                    const getExtraPrice = () => {
                      if (id === 'dips') return calculateDipPrice().toFixed(2);
                      if (id === 'coffee') return coffeeSize === '1kg' ? '35.00' : '15.00';
                      if (id === 'branding') return (maxDonuts * 1.00).toFixed(2);
                      return extra?.price.toFixed(2) || '0.00';
                    };
                    return extra && (
                      <div key={id} className="flex justify-between text-sm">
                        <span>{extra.name} {id === 'branding' && `(${maxDonuts}×1€)`}</span>
                        <span className="font-bold">+{getExtraPrice()}€</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between text-sm">
                    <span>{t.summary.delivery} ({boxCount} {boxCount === 1 ? 'Box' : 'Boxen'})</span>
                    <span className="font-bold">{deliveryCost.toFixed(2)}€</span>
                  </div>
                  {discountPercent > 0 && (
                    <div className="flex justify-between text-sm" style={{ color: BRAND.pink }}>
                      <span>{t.summary.discount} ({discountPercent}%)</span>
                      <span className="font-bold">-{discount.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="pt-4 border-t flex justify-between text-xl" style={{ borderColor: theme.border }}>
                    <span className="font-bold">{t.summary.total}</span>
                    <span className="font-black" style={{ color: BRAND.turquoise }}>{total.toFixed(2)}€</span>
                  </div>
                  <button onClick={() => {
                    // PFLICHT: User muss eingeloggt sein für Checkout
                    if (!user) {
                      setAuthMode('register');
                      setShowAuth(true);
                      toast('Bitte erstelle ein Konto oder melde dich an', { icon: '🔐' });
                      return;
                    }
                    
                    // Prüfe ob alle Schritte ausgefüllt sind
                    if (!interval) {
                      const step3 = document.getElementById('step-3-interval');
                      if (step3) {
                        step3.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        toast('Bitte wähle ein Lieferintervall', { icon: '📅' });
                      }
                      return;
                    }
                    
                    const addressComplete = address.street && address.zip && address.city && address.company;
                    if (!addressComplete) {
                      const step5 = document.getElementById('step-5-address');
                      if (step5) {
                        step5.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        toast('Bitte fülle deine Lieferadresse aus', { icon: '📍' });
                      }
                      return;
                    }
                    
                    // Alle Schritte erledigt - zum Checkout
                    if (!hasSpun && user) {
                      setShowSpinWheel(true);
                    } else {
                      setShowCheckout(true);
                    }
                  }} className="w-full py-4 rounded-xl font-bold text-white mt-4" style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
                  data-testid="checkout-button">
                    {user ? 'Zur Kasse' : 'Anmelden & bestellen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EINHEITLICHER STICKY FOOTER - Immer sichtbar wenn Donuts ausgewählt oder Mystery Box */}
      {(selectedDonutCount > 0 || autoMix) && !showCheckout && (
        <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pt-2"
          style={{ background: 'linear-gradient(to top, rgba(10,10,10,1) 70%, transparent)' }}>
          <div className="max-w-4xl mx-auto rounded-2xl p-3 sm:p-4" 
            style={{ 
              backgroundColor: darkMode ? 'rgba(15,15,18,0.98)' : 'rgba(255,255,255,0.98)', 
              backdropFilter: 'blur(20px)',
              border: `2px solid ${(autoMix || selectedDonutCount >= maxDonuts) ? BRAND.turquoise : 'rgba(255,255,255,0.1)'}`,
              boxShadow: (autoMix || selectedDonutCount >= maxDonuts) 
                ? `0 -10px 40px rgba(0,0,0,0.5), 0 0 30px ${BRAND.turquoise}30` 
                : '0 -10px 40px rgba(0,0,0,0.5)'
            }}>
            <div className="flex items-center justify-between gap-3">
              {/* Linke Seite: Auswahl Status */}
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}20, ${BRAND.pink}20)` }}>
                  <Package className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: BRAND.turquoise }} />
                </div>
                <div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-lg sm:text-2xl font-black" style={{ color: (autoMix || selectedDonutCount >= maxDonuts) ? BRAND.turquoise : BRAND.yellow }}>
                      {autoMix ? maxDonuts : selectedDonutCount}
                    </span>
                    <span className="text-sm sm:text-lg" style={{ color: theme.textMuted }}>/ {maxDonuts}</span>
                    <span className="hidden sm:inline text-sm" style={{ color: theme.textMuted }}>
                      {language === 'de' ? 'ausgewählt' : 'selected'}
                    </span>
                    {(autoMix || selectedDonutCount >= maxDonuts) && (
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center" 
                        style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] sm:text-xs" style={{ color: theme.textMuted }}>
                    {boxCount} Box{boxCount !== 1 && 'en'} • {autoMix ? 'Mystery' : (selectedDonutCount >= maxDonuts ? 'Fertig!' : `Noch ${maxDonuts - selectedDonutCount}`)}
                  </div>
                </div>
              </div>
              
              {/* Rechte Seite: Preis + Button */}
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="text-right">
                  <div className="text-[10px] sm:text-xs" style={{ color: theme.textMuted }}>Gesamt</div>
                  <div className="text-lg sm:text-2xl font-black" style={{ color: BRAND.turquoise }}>{total.toFixed(2)}€</div>
                </div>
                <button 
                  onClick={() => {
                    // Prüfe welcher Schritt noch fehlt und scrolle dorthin
                    if (!autoMix && selectedDonutCount < maxDonuts) {
                      toast(`Noch ${maxDonuts - selectedDonutCount} Donuts auswählen`, { icon: '🍩' });
                      return;
                    }
                    
                    // Schritt 3: Lieferintervall - prüfen ob gewählt
                    if (!interval) {
                      const step3 = document.getElementById('step-3-interval');
                      if (step3) {
                        step3.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        toast('Bitte wähle ein Lieferintervall', { icon: '📅' });
                      }
                      return;
                    }
                    
                    // Schritt 5: Adresse - prüfen ob ausgefüllt
                    const addressComplete = address.street && address.zip && address.city && address.company;
                    if (!addressComplete) {
                      const step5 = document.getElementById('step-5-address');
                      if (step5) {
                        step5.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        toast('Bitte fülle deine Lieferadresse aus', { icon: '📍' });
                      }
                      return;
                    }
                    
                    // Alle Schritte erledigt - zum Checkout
                    if (user) {
                      if (!hasSpun) {
                        setShowSpinWheel(true);
                      } else {
                        setShowCheckout(true);
                      }
                    } else {
                      setShowAuth(true);
                    }
                  }}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm text-white transition-all hover:scale-105 whitespace-nowrap"
                  style={{ 
                    background: (autoMix || selectedDonutCount >= maxDonuts) 
                      ? `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` 
                      : 'rgba(255,255,255,0.15)',
                    boxShadow: (autoMix || selectedDonutCount >= maxDonuts) ? `0 4px 20px ${BRAND.turquoise}40` : 'none'
                  }}>
                  {(autoMix || selectedDonutCount >= maxDonuts) 
                    ? (language === 'de' ? 'Weiter →' : 'Continue →')
                    : `Noch ${maxDonuts - selectedDonutCount}`
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CHECKOUT MODAL - REDESIGNED */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backgroundColor: '#0a0a0a' }}>
          {/* Animated Background */}
          <div className="fixed inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20" 
              style={{ background: `radial-gradient(circle, ${BRAND.turquoise}, transparent)` }} />
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-20" 
              style={{ background: `radial-gradient(circle, ${BRAND.pink}, transparent)` }} />
          </div>

          {/* Checkout Header - Fixed at top */}
          <header className="fixed top-0 left-0 right-0 z-20 px-6 py-4" style={{ backgroundColor: 'rgba(10,10,10,0.98)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${theme.border}` }}>
            <div className="max-w-5xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={() => setShowCheckout(false)} className="p-3 rounded-xl transition-all hover:scale-105" 
                  style={{ backgroundColor: `${BRAND.turquoise}15`, border: `1px solid ${BRAND.turquoise}30` }}>
                  <ArrowRight className="w-5 h-5 rotate-180" style={{ color: BRAND.turquoise }} />
                </button>
                <div>
                  <h1 className="text-xl font-black">Checkout</h1>
                  <p className="text-xs" style={{ color: theme.textMuted }}>Nur noch ein Schritt!</p>
                </div>
              </div>
              {/* Progress Steps */}
              <div className="hidden md:flex items-center gap-2">
                {['Auswahl', 'Details', 'Zahlung'].map((step, i) => (
                  <div key={step} className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i < 2 ? 'text-white' : ''}`}
                      style={{ 
                        background: i < 2 ? `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` : 'transparent',
                        border: i === 2 ? `2px solid ${BRAND.turquoise}` : 'none',
                        color: i === 2 ? BRAND.turquoise : 'white'
                      }}>
                      {i < 2 ? <Check className="w-4 h-4" /> : i + 1}
                    </div>
                    {i < 2 && <div className="w-8 h-0.5" style={{ backgroundColor: BRAND.turquoise }} />}
                  </div>
                ))}
              </div>
              <img 
                src="https://customer-assets.emergentagent.com/job_37cca134-41a5-49ca-9252-6c0a7dd12fc1/artifacts/rsel26pf_Fancy_Donuts_Wortmarke.png" 
                alt="Fancy Donuts" 
                className="h-8 w-auto"
              />
            </div>
          </header>

          {/* Main Content - With top padding for fixed header */}
          <div className="max-w-5xl mx-auto px-6 pt-24 pb-8 relative">
            <div className="grid lg:grid-cols-12 gap-8">
              
              {/* Left Side: Order Summary - Visually Enhanced */}
              <div className="lg:col-span-5">
                <div className="lg:sticky lg:top-24">
                  {/* Order Card with Gradient Border */}
                  <div className="relative rounded-3xl overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}30, ${BRAND.pink}30)`, padding: '1px' }}>
                    <div className="rounded-3xl p-6" style={{ backgroundColor: '#0f0f0f' }}>
                      
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center" 
                            style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                            <ShoppingBag className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-black text-lg">Deine Bestellung</h3>
                            <p className="text-xs" style={{ color: theme.textMuted }}>{boxCount} {boxCount === 1 ? 'Box' : 'Boxen'} • {autoMix ? maxDonuts : selectedDonutCount} Donuts</p>
                          </div>
                        </div>
                        <button onClick={() => setShowCheckout(false)} className="text-xs px-3 py-1 rounded-full"
                          style={{ backgroundColor: `${BRAND.turquoise}15`, color: BRAND.turquoise }}>
                          Ändern
                        </button>
                      </div>

                      {/* Items List */}
                      <div className="space-y-3 mb-6">
                        {/* Donuts - Aufklappbar */}
                        <DonutDetailAccordion 
                          selectedDonuts={selectedDonuts}
                          autoMix={autoMix}
                          maxDonuts={maxDonuts}
                          selectedDonutCount={selectedDonutCount}
                          donutPrice={donutPrice}
                          donuts={donutProducts}
                          t={t}
                          theme={theme}
                          BRAND={BRAND}
                        />

                        {/* Extras */}
                        {selectedExtras.map(id => {
                          const extra = EXTRAS.find(e => e.id === id);
                          const getPrice = () => {
                            if (id === 'dips') return calculateDipPrice().toFixed(2);
                            if (id === 'coffee') return coffeeSize === '1kg' ? '35.00' : '15.00';
                            if (id === 'branding') return (maxDonuts * 1.00).toFixed(2);
                            return extra?.price.toFixed(2) || '0.00';
                          };
                          const IconComponent = extra?.icon || Gift;
                          return extra && (
                            <div key={id} className="flex items-center gap-4 p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                              <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}15` }}>
                                <IconComponent className="w-7 h-7" style={{ color: BRAND.pink }} />
                              </div>
                              <div className="flex-1">
                                <div className="font-bold">{extra.name}</div>
                                <div className="text-xs" style={{ color: theme.textMuted }}>
                                  {id === 'branding' && `${maxDonuts}× 1€`}
                                  {id === 'coffee' && (coffeeSize === '1kg' ? '1kg Packung' : '500g Packung')}
                                  {id === 'dips' && `${Object.values(dipQuantities).reduce((a,b) => a+b, 0)} Dips`}
                                  {id === 'card' && 'A6 Karte'}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-black" style={{ color: BRAND.pink }}>+{getPrice()}€</div>
                              </div>
                            </div>
                          );
                        })}

                        {/* Delivery */}
                        <div className="flex items-center gap-4 p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                          <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                            <Truck className="w-7 h-7" style={{ color: BRAND.yellow }} />
                          </div>
                          <div className="flex-1">
                            <div className="font-bold">Express-Lieferung</div>
                            <div className="text-xs" style={{ color: theme.textMuted }}>{boxCount} {boxCount === 1 ? 'Box' : 'Boxen'} × 9,99€</div>
                          </div>
                          <div className="text-right">
                            <div className="font-black" style={{ color: BRAND.yellow }}>{deliveryCost.toFixed(2)}€</div>
                          </div>
                        </div>
                      </div>

                      {/* Discount */}
                      {discountPercent > 0 && (
                        <div className="flex items-center justify-between p-3 rounded-xl mb-4" style={{ backgroundColor: `${BRAND.pink}10`, border: `1px dashed ${BRAND.pink}40` }}>
                          <div className="flex items-center gap-2">
                            <Tag className="w-5 h-5" style={{ color: BRAND.pink }} />
                            <span className="font-bold" style={{ color: BRAND.pink }}>Abo-Rabatt ({discountPercent}%)</span>
                          </div>
                          <span className="font-black" style={{ color: BRAND.pink }}>-{discount.toFixed(2)}€</span>
                        </div>
                      )}

                      {/* Total */}
                      <div className="pt-4 border-t" style={{ borderColor: theme.border }}>
                        <div className="flex items-center justify-between mb-2">
                          <span style={{ color: theme.textMuted }}>Zwischensumme</span>
                          <span className="font-bold">{(donutPrice + extrasPrice + deliveryCost).toFixed(2)}€</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-black">Gesamt</span>
                          <div className="text-right">
                            <span className="text-3xl font-black" style={{ 
                              background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}>{total.toFixed(2)}€</span>
                            <p className="text-xs" style={{ color: theme.textMuted }}>inkl. MwSt.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trust Badges */}
                  <div className="mt-4 flex items-center justify-center gap-4 text-xs" style={{ color: theme.textMuted }}>
                    <div className="flex items-center gap-1">
                      <Shield className="w-4 h-4" style={{ color: BRAND.turquoise }} />
                      <span>SSL-Verschlüsselung</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Truck className="w-4 h-4" style={{ color: BRAND.pink }} />
                      <span>Express-Versand</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side: Payment Methods */}
              <div className="lg:col-span-7 space-y-6">
                
                {/* Delivery Address */}
                <div className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                      <MapPin className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                    </div>
                    <div>
                      <h3 className="font-black">Lieferadresse</h3>
                      {address.company && <p className="text-xs" style={{ color: theme.textMuted }}>Geschäftsadresse</p>}
                    </div>
                  </div>
                  {address.street ? (
                    <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                      <p className="font-bold">{address.company || 'Firma'}</p>
                      <p className="text-sm" style={{ color: theme.textMuted }}>{address.street} {address.number}</p>
                      <p className="text-sm" style={{ color: theme.textMuted }}>{address.zip} {address.city}</p>
                      <button 
                        onClick={() => setAddress({ ...address, street: '', number: '', zip: '', city: '' })} 
                        className="mt-3 text-xs underline" 
                        style={{ color: BRAND.turquoise }}
                      >
                        Adresse ändern
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <input 
                        placeholder="Firmenname *" 
                        value={address.company} 
                        onChange={(e) => setAddress({ ...address, company: e.target.value })}
                        className="w-full p-3 rounded-xl outline-none text-sm" 
                        style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }}
                      />
                      <div className="grid grid-cols-4 gap-2">
                        <input 
                          placeholder="Straße *" 
                          value={address.street} 
                          onChange={(e) => setAddress({ ...address, street: e.target.value })}
                          className="col-span-3 p-3 rounded-xl outline-none text-sm" 
                          style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                        <input 
                          placeholder="Nr." 
                          value={address.number} 
                          onChange={(e) => setAddress({ ...address, number: e.target.value })}
                          className="p-3 rounded-xl outline-none text-sm" 
                          style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <input 
                          placeholder="PLZ *" 
                          value={address.zip} 
                          onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                          className="p-3 rounded-xl outline-none text-sm" 
                          style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                        <input 
                          placeholder="Stadt *" 
                          value={address.city} 
                          onChange={(e) => setAddress({ ...address, city: e.target.value })}
                          className="col-span-2 p-3 rounded-xl outline-none text-sm" 
                          style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                      </div>
                      
                      {/* E-Mail & Passwort für Kontoerstellung */}
                      {!user && (
                        <>
                          <div className="pt-4 border-t" style={{ borderColor: theme.border }}>
                            <label className="flex items-center gap-3 mb-4 cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={address.createAccount !== false}
                                onChange={(e) => setAddress({ ...address, createAccount: e.target.checked })}
                                className="w-5 h-5 rounded accent-pink-500"
                              />
                              <span className="text-sm font-medium">Konto erstellen für schnellere Bestellungen</span>
                            </label>
                            
                            <input 
                              type="email"
                              placeholder="E-Mail-Adresse *" 
                              value={address.email || ''} 
                              onChange={(e) => setAddress({ ...address, email: e.target.value })}
                              className="w-full p-3 rounded-xl outline-none text-sm mb-2" 
                              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }}
                            />
                            
                            {address.createAccount !== false && (
                              <input 
                                type="password"
                                placeholder="Passwort (min. 6 Zeichen) *" 
                                value={address.password || ''} 
                                onChange={(e) => setAddress({ ...address, password: e.target.value })}
                                className="w-full p-3 rounded-xl outline-none text-sm" 
                                style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Payment Methods */}
                <div className="rounded-2xl p-6" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}15` }}>
                      <CreditCard className="w-5 h-5" style={{ color: BRAND.pink }} />
                    </div>
                    <div>
                      <h3 className="font-black">Zahlungsmethode</h3>
                      <p className="text-xs" style={{ color: theme.textMuted }}>Wähle deine bevorzugte Methode</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {/* Kreditkarte */}
                    <button 
                      onClick={() => setSelectedPaymentMethod('card')}
                      className="p-4 rounded-xl transition-all hover:scale-[1.02]"
                      style={{ 
                        backgroundColor: selectedPaymentMethod === 'card' ? `${BRAND.turquoise}15` : 'rgba(255,255,255,0.03)',
                        border: `2px solid ${selectedPaymentMethod === 'card' ? BRAND.turquoise : theme.border}`
                      }}
                    >
                      <div className="w-12 h-8 rounded-lg mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a1f71, #4169e1)' }}>
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div className="font-bold text-sm">Kreditkarte</div>
                      <div className="text-xs" style={{ color: theme.textMuted }}>Visa, Mastercard</div>
                    </button>

                    {/* PayPal */}
                    <button 
                      onClick={() => setSelectedPaymentMethod('paypal')}
                      className="p-4 rounded-xl transition-all hover:scale-[1.02]"
                      style={{ 
                        backgroundColor: selectedPaymentMethod === 'paypal' ? `${BRAND.turquoise}15` : 'rgba(255,255,255,0.03)',
                        border: `2px solid ${selectedPaymentMethod === 'paypal' ? BRAND.turquoise : theme.border}`
                      }}
                    >
                      <div className="w-12 h-8 rounded-lg mb-3 flex items-center justify-center" style={{ backgroundColor: '#003087' }}>
                        <span className="text-white text-xs font-black">Pay<span style={{ color: '#009cde' }}>Pal</span></span>
                      </div>
                      <div className="font-bold text-sm">PayPal</div>
                      <div className="text-xs" style={{ color: theme.textMuted }}>Mit Konto bezahlen</div>
                    </button>

                    {/* Apple Pay */}
                    <button 
                      onClick={() => setSelectedPaymentMethod('apple_pay')}
                      className="p-4 rounded-xl transition-all hover:scale-[1.02]"
                      style={{ 
                        backgroundColor: selectedPaymentMethod === 'apple_pay' ? `${BRAND.turquoise}15` : 'rgba(255,255,255,0.03)',
                        border: `2px solid ${selectedPaymentMethod === 'apple_pay' ? BRAND.turquoise : theme.border}`
                      }}
                    >
                      <div className="w-12 h-8 rounded-lg mb-3 bg-black flex items-center justify-center">
                        <span className="text-white text-lg"></span>
                      </div>
                      <div className="font-bold text-sm">Apple Pay</div>
                      <div className="text-xs" style={{ color: theme.textMuted }}>Face ID / Touch ID</div>
                    </button>

                    {/* SEPA */}
                    <button 
                      onClick={() => setSelectedPaymentMethod('sepa')}
                      className="p-4 rounded-xl transition-all hover:scale-[1.02]"
                      style={{ 
                        backgroundColor: selectedPaymentMethod === 'sepa' ? `${BRAND.turquoise}15` : 'rgba(255,255,255,0.03)',
                        border: `2px solid ${selectedPaymentMethod === 'sepa' ? BRAND.turquoise : theme.border}`
                      }}
                    >
                      <div className="w-12 h-8 rounded-lg mb-3 flex items-center justify-center" style={{ backgroundColor: '#1a3d7c' }}>
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <div className="font-bold text-sm">SEPA</div>
                      <div className="text-xs" style={{ color: theme.textMuted }}>Lastschrift</div>
                    </button>
                  </div>

                  {/* Selected Method Indicator */}
                  {selectedPaymentMethod && (
                    <div className="flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: `${BRAND.turquoise}10` }}>
                      <Check className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                      <span className="text-sm" style={{ color: BRAND.turquoise }}>
                        {selectedPaymentMethod === 'card' && 'Kreditkarte ausgewählt'}
                        {selectedPaymentMethod === 'paypal' && 'PayPal ausgewählt'}
                        {selectedPaymentMethod === 'apple_pay' && 'Apple Pay ausgewählt'}
                        {selectedPaymentMethod === 'sepa' && 'SEPA-Lastschrift ausgewählt'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Final CTA */}
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      if (!selectedPaymentMethod) {
                        toast.error('Bitte wähle eine Zahlungsmethode');
                        return;
                      }
                      if (!address.street) {
                        toast.error('Bitte gib eine Lieferadresse an');
                        return;
                      }
                      handleOrder();
                    }}
                    disabled={!selectedPaymentMethod || !address.street || !user}
                    className="w-full py-5 rounded-2xl font-black text-white text-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-3"
                    style={{ 
                      background: selectedPaymentMethod && address.street && user
                        ? `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` 
                        : theme.border,
                      boxShadow: selectedPaymentMethod && address.street && user ? `0 10px 40px ${BRAND.turquoise}30` : 'none'
                    }}
                    data-testid="final-checkout-btn"
                  >
                    <Lock className="w-5 h-5" />
                    {!user 
                      ? 'Bitte zuerst anmelden'
                      : selectedPaymentMethod && address.street 
                        ? `Jetzt sicher bezahlen - ${total.toFixed(2)}€`
                        : 'Bitte alle Felder ausfüllen'}
                  </button>

                  {/* Security & Legal */}
                  <div className="text-center space-y-2">
                    <div className="flex items-center justify-center gap-4 text-xs" style={{ color: theme.textMuted }}>
                      <div className="flex items-center gap-1">
                        <Shield className="w-4 h-4" style={{ color: BRAND.turquoise }} />
                        <span>256-bit SSL</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Lock className="w-4 h-4" style={{ color: BRAND.pink }} />
                        <span>PCI-DSS konform</span>
                      </div>
                    </div>
                    <p className="text-xs" style={{ color: theme.textMuted }}>
                      Mit dem Kauf akzeptierst du unsere{' '}
                      <button onClick={() => { setShowCheckout(false); setLegalPage('agb'); }} className="underline" style={{ color: BRAND.turquoise }}>AGB</button>
                      {' '}und{' '}
                      <button onClick={() => { setShowCheckout(false); setLegalPage('datenschutz'); }} className="underline" style={{ color: BRAND.turquoise }}>Datenschutzerklärung</button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BENEFITS - Refactored Component */}
      <BenefitsSection theme={theme} scrollToConfigurator={scrollToConfigurator} />

      {/* TRUST ELEMENTE - Refactored Component */}
      <TrustSection theme={theme} />

      {/* MENGENRABATTE & INCENTIVES */}
      <section className="py-24" style={{ backgroundColor: theme.bgSecondary }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block mb-4 px-5 py-2 rounded-full text-sm font-semibold" style={{ background: `${BRAND.yellow}15`, border: `1px solid ${BRAND.yellow}30`, color: BRAND.yellow }}>
              Mehr sparen
            </span>
            <h2 className="text-3xl md:text-4xl font-black">Je regelmäßiger, desto günstiger</h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { interval: 'Einmalig', discount: '0%', desc: 'Perfekt zum Testen', color: theme.textMuted },
              { interval: 'Monatlich', discount: '5%', desc: 'Ideal für kleine Teams', color: BRAND.turquoise },
              { interval: 'Alle 2 Wochen', discount: '10%', desc: 'Beliebteste Wahl', color: BRAND.pink, popular: true },
              { interval: 'Wöchentlich', discount: '15%', desc: 'Maximum sparen', color: BRAND.yellow },
            ].map((item, i) => (
              <div key={i} className={`relative p-6 rounded-2xl text-center ${item.popular ? 'ring-2' : ''}`} 
                style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, ringColor: BRAND.pink }}>
                {item.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: BRAND.pink }}>
                    BELIEBT
                  </div>
                )}
                <div className="text-4xl font-black mb-2" style={{ color: item.color }}>{item.discount}</div>
                <div className="font-bold mb-1">{item.interval}</div>
                <div className="text-xs" style={{ color: theme.textMuted }}>{item.desc}</div>
              </div>
            ))}
          </div>

          {/* Großkunden-Banner */}
          <div className="p-8 rounded-3xl text-center" style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}20, ${BRAND.pink}20)`, border: `1px solid ${theme.border}` }}>
            <h3 className="text-2xl font-black mb-3">Großkunden-Programm</h3>
            <p className="mb-6" style={{ color: theme.textMuted }}>Ab 50 Mitarbeitern: Individuelle Konditionen, persönlicher Ansprechpartner, exklusive Vorteile</p>
            <button onClick={scrollToConfigurator} className="px-8 py-3 rounded-full font-bold text-white transition-all hover:scale-105" style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
              Großkunden-Angebot anfragen
            </button>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - Refactored Component */}
      <TestimonialsSection 
        theme={theme} 
        t={t} 
        language={language} 
        scrollToConfigurator={scrollToConfigurator} 
      />

      {/* FAQ - Refactored Component */}
      <FAQSection 
        theme={theme} 
        t={t} 
        openFaq={openFaq} 
        setOpenFaq={setOpenFaq} 
        faqRef={faqRef} 
      />

      {/* FINAL CTA - Refactored Component */}
      <FinalCTASection 
        theme={theme} 
        t={t} 
        scrollToConfigurator={scrollToConfigurator} 
      />

      {/* FOOTER - Refactored Component */}
      <Footer 
        theme={theme}
        language={language}
        t={t}
        setLegalPage={setLegalPage}
        darkMode={darkMode}
      />

      {/* STICKY PREISÜBERSICHT - ENTFERNT (doppelt) */}

      {/* SCROLL TO TOP BUTTON */}
      {/* AUTH MODAL - Mobile-optimiert */}
      {showAuth && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowAuth(false)} />
          <div className="relative z-10 w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto" 
            style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
            
            {/* Drag Handle für Mobile */}
            <div className="sm:hidden w-12 h-1 rounded-full mx-auto mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
            
            <button onClick={() => setShowAuth(false)} className="absolute top-4 right-4 p-2.5 rounded-full" 
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <X className="w-5 h-5" />
            </button>
            
            <h2 className="text-xl sm:text-2xl font-black mb-5 sm:mb-6">{authMode === 'login' ? 'Anmelden' : 'Konto erstellen'}</h2>
            
            {/* Google Login Button - größerer Touch-Target */}
            <button
              onClick={() => {
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full py-4 rounded-xl font-bold mb-4 flex items-center justify-center gap-3 transition-all active:scale-[0.98]"
              style={{ backgroundColor: '#fff', color: '#333', border: '1px solid #ddd' }}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Mit Google anmelden
            </button>
            
            <div className="flex items-center gap-4 my-5 sm:my-6">
              <div className="flex-1 h-px" style={{ backgroundColor: theme.border }}></div>
              <span className="text-sm" style={{ color: theme.textMuted }}>oder</span>
              <div className="flex-1 h-px" style={{ backgroundColor: theme.border }}></div>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); const f = e.target; authMode === 'login' ? handleLogin(f.email.value, f.password.value) : handleRegister(f.email.value, f.password.value, f.company.value, f.contact.value); }}>
              {authMode === 'register' && (
                <>
                  <input name="company" placeholder="Firmenname" required 
                    className="w-full p-4 rounded-xl mb-3 outline-none text-base" 
                    style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
                  <input name="contact" placeholder="Ansprechpartner" required 
                    className="w-full p-4 rounded-xl mb-3 outline-none text-base" 
                    style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
                </>
              )}
              <input name="email" type="email" placeholder="E-Mail" required 
                className="w-full p-4 rounded-xl mb-3 outline-none text-base" 
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
              <input name="password" type="password" placeholder="Passwort" required minLength={6} 
                className="w-full p-4 rounded-xl mb-4 outline-none text-base" 
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`, color: theme.text }} />
              
              {authMode === 'login' && (
                <button 
                  type="button" 
                  onClick={() => {
                    const email = document.querySelector('input[name="email"]').value;
                    if (email) {
                      fetch(`${API_URL}/api/auth/forgot-password`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email })
                      }).then(() => toast.success('Falls ein Konto existiert, wurde ein Reset-Link gesendet.'));
                    } else {
                      toast.error('Bitte E-Mail eingeben');
                    }
                  }}
                  className="text-sm mb-4 block py-1"
                  style={{ color: BRAND.turquoise }}
                >
                  Passwort vergessen?
                </button>
              )}
              
              <button type="submit" 
                className="w-full py-4 rounded-xl font-bold text-white mb-4 transition-all active:scale-[0.98]" 
                style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
                data-testid="auth-submit-btn"
              >
                {authMode === 'login' ? 'Anmelden' : 'Registrieren'}
              </button>
            </form>
            <p className="text-center text-sm" style={{ color: theme.textMuted }}>
              {authMode === 'login' ? 'Noch kein Konto? ' : 'Bereits registriert? '}
              <button onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')} className="font-bold py-1" style={{ color: BRAND.turquoise }}>
                {authMode === 'login' ? 'Jetzt registrieren' : 'Anmelden'}
              </button>
            </p>
          </div>
        </div>
      )}

      {/* DASHBOARD */}
      {showDashboard && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backgroundColor: theme.bg }}>
          {/* Dashboard Header */}
          <div className="sticky top-0 z-10 px-6 py-4 flex items-center justify-between" style={{ backgroundColor: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
            <div className="flex items-center gap-4">
              <button onClick={() => setShowDashboard(false)} className="p-3 rounded-xl transition-all hover:scale-105" 
                style={{ backgroundColor: `${BRAND.turquoise}15`, border: `1px solid ${BRAND.turquoise}30` }}>
                <ArrowRight className="w-5 h-5 rotate-180" style={{ color: BRAND.turquoise }} />
              </button>
              <div>
                <h1 className="text-xl font-black">Mein Dashboard</h1>
                {user && <p className="text-sm" style={{ color: theme.textMuted }}>{user.company_name}</p>}
              </div>
            </div>
{/* Logo removed for cleaner dashboard design */}
          </div>

          {dashboardData && (
            <div className="max-w-6xl mx-auto px-6 py-8">
              {/* Begrüßung */}
              <div className="mb-8">
                <h2 className="text-3xl font-black mb-2">Hallo, {user?.company_name || 'Kunde'}! 👋</h2>
                <p style={{ color: theme.textMuted }}>Hier findest du alle Infos zu deinen Bestellungen und Abos.</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="p-5 rounded-2xl" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                    <Package className="w-6 h-6" style={{ color: BRAND.turquoise }} />
                  </div>
                  <div className="text-3xl font-black" style={{ color: BRAND.turquoise }}>{dashboardData.stats?.total_orders || 0}</div>
                  <div className="text-sm" style={{ color: theme.textMuted }}>Bestellungen</div>
                </div>
                <div className="p-5 rounded-2xl" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${BRAND.pink}15` }}>
                    <Calendar className="w-6 h-6" style={{ color: BRAND.pink }} />
                  </div>
                  <div className="text-3xl font-black" style={{ color: BRAND.pink }}>{dashboardData.stats?.active_subscriptions || 0}</div>
                  <div className="text-sm" style={{ color: theme.textMuted }}>Aktive Abos</div>
                </div>
                <div className="p-5 rounded-2xl" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                    <Star className="w-6 h-6" style={{ color: BRAND.yellow }} />
                  </div>
                  <div className="text-3xl font-black" style={{ color: BRAND.yellow }}>{dashboardData.stats?.total_donuts || 0}</div>
                  <div className="text-sm" style={{ color: theme.textMuted }}>Donuts genossen</div>
                </div>
                <div className="p-5 rounded-2xl" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                    <FileText className="w-6 h-6" style={{ color: BRAND.turquoise }} />
                  </div>
                  <div className="text-3xl font-black">{dashboardData.stats?.total_spent?.toFixed(0) || 0}€</div>
                  <div className="text-sm" style={{ color: theme.textMuted }}>Gesamtausgaben</div>
                </div>
              </div>

              {/* Letzte Bestellungen - mit Details */}
              {dashboardData.orders?.length > 0 && (
                <div className="mb-8 rounded-2xl overflow-hidden" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <h3 className="text-lg font-black">Letzte Bestellungen</h3>
                    <span className="text-sm" style={{ color: theme.textMuted }}>{dashboardData.orders.length} gesamt</span>
                  </div>
                  <div className="divide-y" style={{ borderColor: theme.border }}>
                    {dashboardData.orders.slice(0, 5).map(order => (
                      <div key={order.id} className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ 
                              backgroundColor: order.status === 'paid' ? 'rgba(34,197,94,0.15)' : 
                                             order.status === 'shipped' ? 'rgba(139,92,246,0.15)' :
                                             order.status === 'delivered' ? 'rgba(16,185,129,0.15)' : `${BRAND.yellow}15` 
                            }}>
                              <Package className="w-5 h-5" style={{ 
                                color: order.status === 'paid' ? '#22c55e' : 
                                       order.status === 'shipped' ? '#8b5cf6' :
                                       order.status === 'delivered' ? '#10b981' : BRAND.yellow 
                              }} />
                            </div>
                            <div>
                              <div className="font-bold">{order.total?.toFixed(2) || order.final_total?.toFixed(2)}€</div>
                              <div className="text-xs" style={{ color: theme.textMuted }}>
                                {new Date(order.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            order.status === 'paid' ? 'bg-green-500/20 text-green-500' : 
                            order.status === 'shipped' ? 'bg-purple-500/20 text-purple-500' :
                            order.status === 'delivered' ? 'bg-emerald-500/20 text-emerald-500' :
                            order.status === 'processing' ? 'bg-blue-500/20 text-blue-500' :
                            'bg-yellow-500/20 text-yellow-500'
                          }`}>
                            {order.status === 'paid' ? '✓ Bezahlt' : 
                             order.status === 'shipped' ? '📦 Versendet' :
                             order.status === 'delivered' ? '✓ Geliefert' :
                             order.status === 'processing' ? '⏳ In Bearbeitung' :
                             'Offen'}
                          </span>
                        </div>
                        {/* Bestellte Artikel */}
                        {order.items && order.items.length > 0 && (
                          <div className="mt-3 p-3 rounded-xl space-y-2" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                            {order.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>{item.quantity}x {item.name}</span>
                                <span style={{ color: BRAND.turquoise }}>{(item.price * item.quantity).toFixed(2)}€</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Lieferadresse */}
                        {order.delivery_address && (
                          <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: theme.textMuted }}>
                            <MapPin className="w-3 h-3" />
                            <span>{order.delivery_address}</span>
                          </div>
                        )}
                        {/* Rechnung Download Button - nur für bezahlte Bestellungen */}
                        {order.status === 'paid' && (
                          <div className="mt-3 flex gap-2">
                            <button
                              onClick={async () => {
                                try {
                                  // First try to get Stripe invoice
                                  const invoiceData = dashboardData.invoices?.find(inv => inv.order_id === order.id || inv.id === order.id);
                                  if (invoiceData?.pdf_url) {
                                    window.open(invoiceData.pdf_url, '_blank');
                                  } else {
                                    // Fallback: Generate PDF from backend
                                    const response = await fetch(`${API_URL}/api/user/orders/${order.id}/invoice`, {
                                      headers: { 'Authorization': `Bearer ${token}` }
                                    });
                                    if (response.ok) {
                                      const blob = await response.blob();
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `Rechnung_${order.id.slice(0,8).toUpperCase()}.pdf`;
                                      a.click();
                                      URL.revokeObjectURL(url);
                                    } else {
                                      toast.info('Rechnung wird erstellt...');
                                    }
                                  }
                                } catch (err) {
                                  toast.error('Rechnung konnte nicht geladen werden');
                                }
                              }}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all hover:scale-[1.02]"
                              style={{ backgroundColor: `${BRAND.turquoise}15`, color: BRAND.turquoise }}
                              data-testid={`invoice-download-${order.id}`}
                            >
                              <Download className="w-3 h-3" />
                              Rechnung
                            </button>
                          </div>
                        )}
                        {/* Stornieren Button - nur für neuere Bestellungen (innerhalb 1 Stunde) */}
                        {(order.status === 'paid' || order.status === 'pending') && 
                         order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled' &&
                         (new Date() - new Date(order.created_at)) < 60 * 60 * 1000 && (
                          <div className="mt-3 flex gap-2">
                            <button
                              onClick={async () => {
                                if (!window.confirm('Bestellung wirklich stornieren? Bei bezahlten Bestellungen wird eine Rückerstattung veranlasst.')) return;
                                try {
                                  const response = await apiCall(`/api/orders/${order.id}/cancel`, { method: 'POST' });
                                  if (response.success) {
                                    toast.success(response.message);
                                    fetchDashboard();
                                  }
                                } catch (err) {
                                  toast.error(err.message || 'Stornierung fehlgeschlagen');
                                }
                              }}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all hover:scale-[1.02]"
                              style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444' }}
                              data-testid={`cancel-order-${order.id}`}
                            >
                              <X className="w-3 h-3" />
                              Stornieren
                            </button>
                            <span className="text-xs self-center" style={{ color: theme.textMuted }}>
                              (noch {Math.max(0, Math.floor(60 - (new Date() - new Date(order.created_at)) / 60000))} Min. möglich)
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Meine Abos */}
                <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <h3 className="text-lg font-black">Meine Abos</h3>
                    <button onClick={() => { setShowDashboard(false); scrollToConfigurator('abo'); }} className="px-4 py-2 rounded-lg text-sm font-bold" style={{ backgroundColor: `${BRAND.turquoise}15`, color: BRAND.turquoise }}>
                      + Neues Abo
                    </button>
                  </div>
                  <div className="p-5 space-y-4">
                    {dashboardData.subscriptions?.length > 0 ? dashboardData.subscriptions.map(sub => (
                      <div key={sub.id} className="p-4 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${theme.border}` }}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                              <Package className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <span className="font-bold">{INTERVALS.find(i => i.id === sub.interval)?.name || 'Abo'}</span>
                              <p className="text-xs" style={{ color: theme.textMuted }}>{sub.donuts_count || maxDonuts} Donuts pro Lieferung</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${sub.status === 'active' ? 'bg-green-500/20 text-green-500' : sub.status === 'paused' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-red-500/20 text-red-500'}`}>
                            {sub.status === 'active' ? '● Aktiv' : sub.status === 'paused' ? '● Pausiert' : '● Gekündigt'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-4 p-3 rounded-lg" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                          <span className="text-sm" style={{ color: theme.textMuted }}>Preis pro Lieferung</span>
                          <span className="text-lg font-black" style={{ color: BRAND.turquoise }}>{sub.total_per_delivery?.toFixed(2)}€</span>
                        </div>
                        {sub.status !== 'cancelled' && (
                          <div className="flex gap-2">
                            {sub.status === 'active' && (
                              <button onClick={() => apiCall(`/api/subscriptions/${sub.id}/pause`, { method: 'POST' }).then(fetchDashboard)} className="flex-1 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all hover:scale-[1.02]" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                                <Pause className="w-4 h-4" />Pausieren
                              </button>
                            )}
                            {sub.status === 'paused' && (
                              <button onClick={() => apiCall(`/api/subscriptions/${sub.id}/resume`, { method: 'POST' }).then(fetchDashboard)} className="flex-1 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all hover:scale-[1.02]" style={{ backgroundColor: `${BRAND.turquoise}20`, color: BRAND.turquoise }}>
                                <Play className="w-4 h-4" />Fortsetzen
                              </button>
                            )}
                            <button onClick={() => { if(window.confirm('Abo wirklich kündigen?')) apiCall(`/api/subscriptions/${sub.id}/cancel`, { method: 'POST' }).then(fetchDashboard) }} className="flex-1 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 text-red-500 transition-all hover:scale-[1.02]" style={{ backgroundColor: 'rgba(239,68,68,0.1)' }}>
                              <Trash2 className="w-4 h-4" />Kündigen
                            </button>
                          </div>
                        )}
                      </div>
                    )) : (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-3">🍩</div>
                        <p className="font-bold mb-2">Noch keine Abos</p>
                        <p className="text-sm mb-4" style={{ color: theme.textMuted }}>Starte jetzt dein erstes Donut-Abo!</p>
                        <button onClick={() => { setShowDashboard(false); scrollToConfigurator('abo'); }} className="px-6 py-3 rounded-xl font-bold text-white" style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                          Abo konfigurieren
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rechnungen */}
                <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <h3 className="text-lg font-black">Rechnungen</h3>
                    <span className="text-sm" style={{ color: theme.textMuted }}>{dashboardData.invoices?.length || 0} gesamt</span>
                  </div>
                  <div className="p-5 space-y-3">
                    {dashboardData.invoices?.length > 0 ? dashboardData.invoices.slice(0, 6).map(inv => (
                      <div key={inv.id} className="flex items-center justify-between p-4 rounded-xl transition-all hover:scale-[1.01]" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${theme.border}` }}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: inv.status === 'paid' ? 'rgba(34,197,94,0.15)' : `${BRAND.yellow}15` }}>
                            <FileText className="w-5 h-5" style={{ color: inv.status === 'paid' ? '#22c55e' : BRAND.yellow }} />
                          </div>
                          <div>
                            <div className="font-bold">{inv.amount?.toFixed(2)}€</div>
                            <div className="text-xs" style={{ color: theme.textMuted }}>{new Date(inv.created_at || inv.created).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${inv.status === 'paid' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                            {inv.status === 'paid' ? '✓ Bezahlt' : 'Offen'}
                          </span>
                          {inv.pdf_url && (
                            <a 
                              href={inv.pdf_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg flex items-center gap-1 text-xs font-bold transition-all hover:scale-105"
                              style={{ backgroundColor: `${BRAND.turquoise}15`, color: BRAND.turquoise }}
                            >
                              <Download className="w-4 h-4" />
                              <span className="hidden sm:inline">PDF</span>
                            </a>
                          )}
                          {inv.hosted_url && (
                            <a 
                              href={inv.hosted_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg transition-all hover:scale-105"
                              style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-3">📄</div>
                        <p className="font-bold mb-2">Noch keine Rechnungen</p>
                        <p className="text-sm" style={{ color: theme.textMuted }}>Nach deiner ersten Bestellung erscheinen hier deine Rechnungen.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Kontoeinstellungen */}
                <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="p-5" style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <h3 className="text-lg font-black">Kontoeinstellungen</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    <button onClick={() => openDashboardSection('profile')} className="w-full flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.01]" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                          <User className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                        </div>
                        <div className="text-left">
                          <div className="font-bold">Profil bearbeiten</div>
                          <div className="text-xs" style={{ color: theme.textMuted }}>{user?.email}</div>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5" style={{ color: theme.textMuted }} />
                    </button>
                    <button onClick={() => openDashboardSection('payment')} className="w-full flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.01]" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}15` }}>
                          <CreditCard className="w-5 h-5" style={{ color: BRAND.pink }} />
                        </div>
                        <div className="text-left">
                          <div className="font-bold">Zahlungsmethoden</div>
                          <div className="text-xs" style={{ color: theme.textMuted }}>{savedPaymentMethods.length > 0 ? `${savedPaymentMethods.length} gespeichert` : 'Kreditkarte verwalten'}</div>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5" style={{ color: theme.textMuted }} />
                    </button>
                    <button onClick={() => openDashboardSection('addresses')} className="w-full flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.01]" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                          <Truck className="w-5 h-5" style={{ color: BRAND.yellow }} />
                        </div>
                        <div className="text-left">
                          <div className="font-bold">Lieferadressen</div>
                          <div className="text-xs" style={{ color: theme.textMuted }}>{savedAddresses.length > 0 ? `${savedAddresses.length} gespeichert` : 'Adressen verwalten'}</div>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5" style={{ color: theme.textMuted }} />
                    </button>
                  </div>
                </div>

                {/* Hilfe & Abmelden */}
                <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
                  <div className="p-5" style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <h3 className="text-lg font-black">Hilfe & Support</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    <button onClick={scrollToFaq} className="w-full flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.01]" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                          <FileText className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                        </div>
                        <div className="text-left">
                          <div className="font-bold">FAQ & Hilfe</div>
                          <div className="text-xs" style={{ color: theme.textMuted }}>Häufige Fragen</div>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5" style={{ color: theme.textMuted }} />
                    </button>
                    <button onClick={() => openDashboardSection('contact')} className="w-full flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.01]" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}15` }}>
                          <FileText className="w-5 h-5" style={{ color: BRAND.pink }} />
                        </div>
                        <div className="text-left">
                          <div className="font-bold">Kontakt</div>
                          <div className="text-xs" style={{ color: theme.textMuted }}>support@fancydonuts.de</div>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5" style={{ color: theme.textMuted }} />
                    </button>
                    
                    <button onClick={handleLogout} className="w-full mt-4 py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all hover:scale-[1.01]" style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
                      <LogOut className="w-5 h-5" />
                      Abmelden
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PROFIL BEARBEITEN MODAL */}
          {dashboardView === 'profile' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="w-full max-w-md rounded-2xl p-6" style={{ backgroundColor: theme.bg }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-black">Profil bearbeiten</h3>
                  <button onClick={() => setDashboardView('main')} className="p-2 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: theme.textMuted }}>Firmenname</label>
                    <input type="text" value={editProfile.company_name} onChange={(e) => setEditProfile({...editProfile, company_name: e.target.value})}
                      className="w-full p-3 rounded-xl outline-none" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: `1px solid ${theme.border}`, color: theme.text }} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: theme.textMuted }}>Ansprechpartner</label>
                    <input type="text" value={editProfile.contact_person} onChange={(e) => setEditProfile({...editProfile, contact_person: e.target.value})}
                      className="w-full p-3 rounded-xl outline-none" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: `1px solid ${theme.border}`, color: theme.text }} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: theme.textMuted }}>E-Mail</label>
                    <input type="email" value={editProfile.email} onChange={(e) => setEditProfile({...editProfile, email: e.target.value})}
                      className="w-full p-3 rounded-xl outline-none" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: `1px solid ${theme.border}`, color: theme.text }} />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setDashboardView('main')} className="flex-1 py-3 rounded-xl font-bold" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                    Abbrechen
                  </button>
                  <button onClick={saveProfile} className="flex-1 py-3 rounded-xl font-bold text-white" style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                    Speichern
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ZAHLUNGSMETHODEN MODAL */}
          {dashboardView === 'payment' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="w-full max-w-md rounded-2xl p-6" style={{ backgroundColor: theme.bg }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-black">Zahlungsmethoden</h3>
                  <button onClick={() => setDashboardView('main')} className="p-2 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {savedPaymentMethods.length > 0 ? (
                  <div className="space-y-3">
                    {savedPaymentMethods.map((pm, i) => (
                      <div key={pm.id || i} className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: `1px solid ${pm.isDefault ? BRAND.turquoise : theme.border}` }}>
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-6 h-6" style={{ color: pm.isDefault ? BRAND.turquoise : theme.textMuted }} />
                          <div>
                            <div className="font-bold flex items-center gap-2">
                              •••• {pm.last4}
                              {pm.isDefault && <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${BRAND.turquoise}20`, color: BRAND.turquoise }}>Standard</span>}
                            </div>
                            <div className="text-xs" style={{ color: theme.textMuted }}>{pm.brand} • Läuft ab {pm.exp}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!pm.isDefault && (
                            <button 
                              onClick={async () => {
                                try {
                                  await apiCall(`/api/payments/payment-methods/${pm.id}/default`, { method: 'POST' });
                                  toast.success('Standardkarte geändert');
                                  fetchDashboard();
                                } catch (e) { toast.error(e.message); }
                              }}
                              className="text-xs px-2 py-1 rounded-lg"
                              style={{ backgroundColor: `${BRAND.turquoise}15`, color: BRAND.turquoise }}
                            >
                              Als Standard
                            </button>
                          )}
                          <button 
                            onClick={async () => {
                              if (window.confirm('Karte wirklich entfernen?')) {
                                try {
                                  await apiCall(`/api/payments/payment-methods/${pm.id}`, { method: 'DELETE' });
                                  toast.success('Karte entfernt');
                                  fetchDashboard();
                                } catch (e) { toast.error(e.message); }
                              }
                            }}
                            className="text-red-500 text-sm font-medium hover:text-red-400"
                          >
                            Entfernen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${BRAND.pink}15` }}>
                      <CreditCard className="w-8 h-8" style={{ color: BRAND.pink }} />
                    </div>
                    <p className="font-bold mb-2">Keine Zahlungsmethode gespeichert</p>
                    <p className="text-sm mb-4" style={{ color: theme.textMuted }}>Füge eine Karte hinzu, um schneller zu bezahlen.</p>
                  </div>
                )}
                <button 
                  onClick={async () => {
                    try {
                      const data = await apiCall('/api/payments/customer-portal');
                      if (data.url) {
                        // Öffne in neuem Tab statt redirect
                        window.open(data.url, '_blank');
                        toast.success('Stripe Kundenportal geöffnet. Füge dort deine Karte hinzu!');
                      }
                    } catch (e) {
                      toast.info('Bei deiner nächsten Bestellung kannst du eine Karte speichern.');
                    }
                  }}
                  className="w-full mt-4 py-3 rounded-xl font-bold transition-all hover:scale-[1.02]" 
                  style={{ backgroundColor: `${BRAND.turquoise}15`, color: BRAND.turquoise }}
                >
                  + Neue Karte hinzufügen
                </button>
              </div>
            </div>
          )}

          {/* LIEFERADRESSEN MODAL */}
          {dashboardView === 'addresses' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="w-full max-w-md rounded-2xl p-6" style={{ backgroundColor: theme.bg }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-black">Lieferadressen</h3>
                  <button onClick={() => setDashboardView('main')} className="p-2 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {savedAddresses.length > 0 ? (
                  <div className="space-y-3">
                    {savedAddresses.map((addr, i) => (
                      <div key={i} className="p-4 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: `1px solid ${theme.border}` }}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-bold">{addr.company}</div>
                            <div className="text-sm" style={{ color: theme.textMuted }}>
                              {addr.street} {addr.number}<br />
                              {addr.zip} {addr.city}
                            </div>
                          </div>
                          <button className="text-red-500 text-sm font-medium">Entfernen</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                      <Truck className="w-8 h-8" style={{ color: BRAND.yellow }} />
                    </div>
                    <p className="font-bold mb-2">Keine Adresse gespeichert</p>
                    <p className="text-sm mb-4" style={{ color: theme.textMuted }}>Adressen werden automatisch bei deiner Bestellung gespeichert.</p>
                  </div>
                )}
                <button className="w-full mt-4 py-3 rounded-xl font-bold" style={{ backgroundColor: `${BRAND.turquoise}15`, color: BRAND.turquoise }}>
                  + Neue Adresse hinzufügen
                </button>
              </div>
            </div>
          )}

          {/* KONTAKT MODAL */}
          {dashboardView === 'contact' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="w-full max-w-md rounded-2xl p-6" style={{ backgroundColor: theme.bg }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-black">Kontakt</h3>
                  <button onClick={() => setDashboardView('main')} className="p-2 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                    <div className="text-sm font-medium mb-1" style={{ color: theme.textMuted }}>E-Mail</div>
                    <a href="mailto:support@fancydonuts.de" className="font-bold" style={{ color: BRAND.turquoise }}>support@fancydonuts.de</a>
                  </div>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                    <div className="text-sm font-medium mb-1" style={{ color: theme.textMuted }}>Telefon</div>
                    <a href="tel:+4933112345678" className="font-bold" style={{ color: BRAND.turquoise }}>+49 331 12345678</a>
                  </div>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                    <div className="text-sm font-medium mb-1" style={{ color: theme.textMuted }}>Adresse</div>
                    <p className="font-medium">
                      Fancy Donuts GmbH<br />
                      Brandenburger Str. 12<br />
                      14467 Potsdam
                    </p>
                  </div>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                    <div className="text-sm font-medium mb-1" style={{ color: theme.textMuted }}>Öffnungszeiten</div>
                    <p className="font-medium">
                      Mo - Fr: 8:00 - 18:00 Uhr<br />
                      Sa: 9:00 - 14:00 Uhr
                    </p>
                  </div>
                </div>
                <button onClick={() => setDashboardView('main')} className="w-full mt-6 py-3 rounded-xl font-bold" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                  Schließen
                </button>
              </div>
            </div>
          )}
          
          {/* ADMIN LOGIN MODAL - Separates System */}
          {showAdminLogin && !adminLoggedIn && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}>
              <div className="w-full max-w-md rounded-2xl p-8" style={{ backgroundColor: '#1a1a2e', border: '2px solid rgba(139, 92, 246, 0.3)' }}>
                <div className="text-center mb-8">
                  <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}>
                    <Shield className="w-10 h-10" style={{ color: '#8b5cf6' }} />
                  </div>
                  <h2 className="text-2xl font-black text-white">Admin Bereich</h2>
                  <p className="text-sm text-gray-400 mt-2">Nur für autorisierte Mitarbeiter</p>
                </div>
                
                {adminError && (
                  <div className="mb-4 p-3 rounded-lg bg-red-500/20 text-red-400 text-sm text-center">
                    {adminError}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-bold text-gray-400 mb-2 block">E-Mail</label>
                    <input
                      type="email"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      className="w-full p-4 rounded-xl bg-black/30 border-2 border-gray-700 text-white outline-none focus:border-purple-500 transition-colors"
                      placeholder="admin@..."
                    />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-gray-400 mb-2 block">Passwort</label>
                    <input
                      type="password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                      className="w-full p-4 rounded-xl bg-black/30 border-2 border-gray-700 text-white outline-none focus:border-purple-500 transition-colors"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                
                <button
                  onClick={handleAdminLogin}
                  disabled={adminLoading || !adminEmail || !adminPassword}
                  className="w-full mt-6 py-4 rounded-xl font-bold text-white transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' }}
                >
                  {adminLoading ? 'Anmelden...' : 'Admin Login'}
                </button>
                
                <button
                  onClick={() => setShowAdminLogin(false)}
                  className="w-full mt-3 py-3 rounded-xl font-bold text-gray-400 hover:text-white transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          )}
          
          {/* ADMIN DASHBOARD - Volles Panel */}
          {adminLoggedIn && adminData && (
            <div className="fixed inset-0 z-[100] overflow-auto" style={{ backgroundColor: '#0a0a0f' }}>
              {/* Admin Header */}
              <div className="sticky top-0 z-10 px-4 py-4" style={{ backgroundColor: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setDashboardView('main')} className="p-2 rounded-xl" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                      <ArrowRight className="w-5 h-5 rotate-180" style={{ color: '#8b5cf6' }} />
                    </button>
                    <div>
                      <h1 className="text-xl font-black text-white">Admin Dashboard</h1>
                      <p className="text-xs text-gray-500">Fancy Donuts Verwaltung</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}>
                      <Shield className="w-4 h-4" style={{ color: '#8b5cf6' }} />
                      <span className="text-sm font-bold" style={{ color: '#8b5cf6' }}>Admin</span>
                    </div>
                    <button 
                      onClick={handleAdminLogout}
                      className="p-2 rounded-xl hover:bg-red-500/20 transition-colors"
                    >
                      <LogOut className="w-5 h-5 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="max-w-6xl mx-auto p-4 space-y-6">
                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-5 rounded-2xl" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="text-3xl font-black" style={{ color: BRAND.turquoise }}>{adminData.stats?.total_users || 0}</div>
                    <div className="text-sm font-medium text-gray-400">Registrierte Kunden</div>
                    <div className="text-xs mt-1" style={{ color: BRAND.turquoise }}>+{adminData.stats?.recent_users_7d || 0} diese Woche</div>
                  </div>
                  <div className="p-5 rounded-2xl" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="text-3xl font-black" style={{ color: BRAND.pink }}>{adminData.stats?.total_orders || 0}</div>
                    <div className="text-sm font-medium text-gray-400">Bestellungen</div>
                    <div className="text-xs mt-1" style={{ color: BRAND.pink }}>{adminData.stats?.paid_orders || 0} bezahlt</div>
                  </div>
                  <div className="p-5 rounded-2xl" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="text-3xl font-black" style={{ color: BRAND.yellow }}>{adminData.stats?.total_revenue?.toFixed(2) || '0.00'}€</div>
                    <div className="text-sm font-medium text-gray-400">Umsatz</div>
                    <div className="text-xs mt-1" style={{ color: BRAND.yellow }}>Gesamtumsatz</div>
                  </div>
                  <div className="p-5 rounded-2xl" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="text-3xl font-black" style={{ color: '#8b5cf6' }}>{adminData.newsletter?.length || 0}</div>
                    <div className="text-sm font-medium text-gray-400">Newsletter</div>
                    <div className="text-xs mt-1" style={{ color: '#8b5cf6' }}>E-Mail Adressen</div>
                  </div>
                </div>
                
                {/* Admin Navigation */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {[
                    { id: 'overview', label: 'Übersicht', icon: '📊' },
                    { id: 'users', label: 'Kunden', icon: '👥' },
                    { id: 'orders', label: 'Bestellungen', icon: '📦' },
                    { id: 'newsletter', label: 'Newsletter', icon: '📧' },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setAdminView(tab.id)}
                      className="px-4 py-2 rounded-xl font-bold whitespace-nowrap transition-all"
                      style={{
                        backgroundColor: adminView === tab.id ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                        color: adminView === tab.id ? '#8b5cf6' : '#fff',
                        border: adminView === tab.id ? '2px solid rgba(139, 92, 246, 0.5)' : '2px solid transparent'
                      }}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </div>
                
                {/* Users List */}
                {adminView === 'users' && (
                  <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <h3 className="text-lg font-black text-white">Registrierte Kunden ({adminData.users?.length || 0})</h3>
                      <button onClick={fetchAdminData} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                        <RefreshCw className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    <div className="divide-y divide-white/10">
                      {adminData.users?.map((u, i) => (
                        <div key={i} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg" style={{ backgroundColor: BRAND.turquoise }}>
                              {u.contact_name?.[0] || u.email?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <div className="font-bold text-white">{u.company_name || 'Privatkunde'}</div>
                              <div className="text-sm text-gray-400">{u.email}</div>
                              <div className="text-xs text-gray-500">{u.contact_name}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm font-bold" style={{ color: BRAND.yellow }}>{u.total_orders || 0} Bestellungen</div>
                              <div className="text-xs text-gray-400">{u.total_spent?.toFixed(2) || '0.00'}€ Umsatz</div>
                              <div className="text-xs text-gray-500">{new Date(u.created_at).toLocaleDateString('de-DE')}</div>
                            </div>
                            <button 
                              onClick={() => handleAdminDeleteUser(u.id, u.email)}
                              className="p-2 rounded-lg hover:bg-red-500/20 transition-colors group"
                              title="Kunde löschen"
                            >
                              <Trash2 className="w-4 h-4 text-gray-500 group-hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {(!adminData.users || adminData.users.length === 0) && (
                        <div className="p-8 text-center text-gray-500">
                          Noch keine Kunden registriert
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Orders List */}
                {adminView === 'orders' && (
                  <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <h3 className="text-lg font-black text-white">Bestellungen ({adminData.orders?.length || 0})</h3>
                      <div className="flex items-center gap-2">
                        <button onClick={handleAdminExportOrders} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold" style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6' }}>
                          <Download className="w-4 h-4" />
                          CSV Export
                        </button>
                        <button onClick={fetchAdminData} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                          <RefreshCw className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                    </div>
                    <div className="divide-y divide-white/10">
                      {adminData.orders?.map((order, i) => (
                        <div key={i} className="p-4 hover:bg-white/5 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm font-bold" style={{ color: BRAND.turquoise }}>
                                #{order.id?.slice(0, 8).toUpperCase()}
                              </span>
                              <select 
                                value={order.status || 'pending'}
                                onChange={(e) => handleAdminUpdateOrderStatus(order.id, e.target.value)}
                                className={`px-2 py-0.5 rounded-full text-xs font-bold border-0 cursor-pointer ${
                                  order.status === 'paid' ? 'bg-green-500/20 text-green-500' :
                                  order.status === 'processing' ? 'bg-blue-500/20 text-blue-500' :
                                  order.status === 'shipped' ? 'bg-purple-500/20 text-purple-500' :
                                  order.status === 'delivered' ? 'bg-emerald-500/20 text-emerald-500' :
                                  order.status === 'cancelled' ? 'bg-red-500/20 text-red-500' :
                                  'bg-yellow-500/20 text-yellow-500'
                                }`}
                              >
                                <option value="pending">Ausstehend</option>
                                <option value="paid">Bezahlt</option>
                                <option value="processing">In Bearbeitung</option>
                                <option value="shipped">Versendet</option>
                                <option value="delivered">Geliefert</option>
                                <option value="cancelled">Storniert</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-lg font-black" style={{ color: BRAND.pink }}>
                                {order.final_total?.toFixed(2)}€
                              </div>
                              <button 
                                onClick={() => handleAdminNotifyStaff(order.id)}
                                className="p-2 rounded-lg hover:bg-blue-500/20 transition-colors group"
                                title="Team benachrichtigen"
                              >
                                <Send className="w-4 h-4 text-gray-500 group-hover:text-blue-500" />
                              </button>
                              <button 
                                onClick={() => handleAdminDeleteOrder(order.id)}
                                className="p-2 rounded-lg hover:bg-red-500/20 transition-colors group"
                                title="Bestellung löschen"
                              >
                                <Trash2 className="w-4 h-4 text-gray-500 group-hover:text-red-500" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm text-gray-400">
                            <div>
                              <span className="font-medium text-white">{order.company_name || 'Privatkunde'}</span>
                              <span className="mx-2">·</span>
                              <span>{order.contact_name}</span>
                            </div>
                            <div>{new Date(order.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                          {order.delivery_address && (
                            <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="flex-1">{order.delivery_address}</span>
                              <button 
                                onClick={() => {
                                  navigator.clipboard.writeText(order.delivery_address);
                                  toast.success('Adresse kopiert!');
                                }}
                                className="px-2 py-1 rounded text-[10px] font-bold hover:bg-white/10 transition-colors"
                                style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6' }}
                              >
                                Kopieren
                              </button>
                            </div>
                          )}
                          {order.items && order.items.length > 0 && (
                            <div className="mt-3 p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                              <div className="text-xs font-bold text-gray-400 mb-2">Bestellte Artikel:</div>
                              <div className="space-y-1">
                                {order.items.map((item, idx) => (
                                  <div key={idx} className="flex justify-between text-xs">
                                    <span className="text-white">{item.quantity}x {item.name}</span>
                                    <span style={{ color: BRAND.turquoise }}>{(item.price * item.quantity).toFixed(2)}€</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {order.staff_notified && (
                            <div className="mt-2 text-xs text-green-500 flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Team wurde benachrichtigt
                            </div>
                          )}
                        </div>
                      ))}
                      {(!adminData.orders || adminData.orders.length === 0) && (
                        <div className="p-8 text-center text-gray-500">
                          Noch keine Bestellungen
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Newsletter List */}
                {adminView === 'newsletter' && (
                  <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <h3 className="text-lg font-black text-white">Newsletter E-Mails ({adminData.newsletter?.length || 0})</h3>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => {
                            const emails = adminData.newsletter?.map(n => n.email).join('\n');
                            navigator.clipboard.writeText(emails);
                            toast.success('E-Mails kopiert!');
                          }}
                          className="px-3 py-1.5 rounded-lg text-sm font-bold"
                          style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6' }}
                        >
                          Alle kopieren
                        </button>
                        <button onClick={handleAdminExportNewsletter} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }}>
                          <Download className="w-4 h-4" />
                          CSV
                        </button>
                      </div>
                    </div>
                    <div className="divide-y divide-white/10">
                      {adminData.newsletter?.map((sub, i) => (
                        <div key={i} className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}20` }}>
                              <Mail className="w-5 h-5" style={{ color: BRAND.pink }} />
                            </div>
                            <div>
                              <div className="font-medium text-white">{sub.email}</div>
                              <div className="text-xs text-gray-500">
                                {sub.source === 'spin_wheel' ? '🎡 Glücksrad' : sub.source}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            {sub.discount_code && (
                              <div className="text-sm font-bold" style={{ color: BRAND.turquoise }}>
                                {sub.discount_percent}% - {sub.discount_code}
                              </div>
                            )}
                            <div className="text-xs text-gray-500">
                              {new Date(sub.subscribed_at).toLocaleDateString('de-DE')}
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!adminData.newsletter || adminData.newsletter.length === 0) && (
                        <div className="p-8 text-center text-gray-500">
                          Noch keine Newsletter-Anmeldungen
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Overview - Recent Activity */}
                {adminView === 'overview' && (
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Recent Users */}
                    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <h3 className="font-black text-white">Neueste Kunden</h3>
                        <button onClick={() => setAdminView('users')} className="text-sm font-bold" style={{ color: '#8b5cf6' }}>
                          Alle anzeigen →
                        </button>
                      </div>
                      <div className="divide-y divide-white/10">
                        {adminData.users?.slice(0, 5).map((u, i) => (
                          <div key={i} className="p-3 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: BRAND.turquoise }}>
                              {u.contact_name?.[0] || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-white truncate">{u.company_name || u.email}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Recent Newsletter */}
                    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <h3 className="font-black text-white">Neueste E-Mails</h3>
                        <button onClick={() => setAdminView('newsletter')} className="text-sm font-bold" style={{ color: '#8b5cf6' }}>
                          Alle anzeigen →
                        </button>
                      </div>
                      <div className="divide-y divide-white/10">
                        {adminData.newsletter?.slice(0, 5).map((n, i) => (
                          <div key={i} className="p-3 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}20` }}>
                              <Mail className="w-4 h-4" style={{ color: BRAND.pink }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-white truncate">{n.email}</div>
                            </div>
                            {n.discount_percent > 0 && (
                              <span className="text-xs font-bold" style={{ color: BRAND.turquoise }}>{n.discount_percent}%</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* EXTRA MODALS */}
      {showExtraModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowExtraModal(null)} />
          <div className="relative w-full max-w-md rounded-2xl p-6" style={{ backgroundColor: theme.bgCard }}>
            <button onClick={() => setShowExtraModal(null)} className="absolute top-4 right-4 p-2 rounded-full" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
              <X className="w-4 h-4" />
            </button>

            {/* COFFEE MODAL */}
            {showExtraModal === 'coffee' && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                    <Coffee className="w-6 h-6" style={{ color: BRAND.yellow }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black">{t.extras.coffee}</h3>
                    <p className="text-sm" style={{ color: theme.textMuted }}>Pareto Kaffeebohnen aus Italien</p>
                  </div>
                </div>
                <div className="space-y-3 mb-6">
                  {[
                    { id: '350g', name: t.extras.coffeeSize350, price: '15€' },
                    { id: '1kg', name: t.extras.coffeeSize1kg, price: '35€' },
                  ].map(size => (
                    <button key={size.id} onClick={() => setCoffeeSize(size.id)}
                      className="w-full p-4 rounded-xl text-left transition-all flex items-center justify-between"
                      style={{ 
                        backgroundColor: coffeeSize === size.id ? `${BRAND.yellow}10` : 'transparent', 
                        border: `2px solid ${coffeeSize === size.id ? BRAND.yellow : theme.border}` 
                      }}>
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${coffeeSize === size.id ? 'text-white' : ''}`}
                          style={{ backgroundColor: coffeeSize === size.id ? BRAND.yellow : (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)') }}>
                          {coffeeSize === size.id && <Check className="w-4 h-4" />}
                        </div>
                        <span className="font-bold">{size.name}</span>
                      </div>
                      <span className="font-black" style={{ color: BRAND.yellow }}>{size.price}</span>
                    </button>
                  ))}
                </div>
                <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: `${BRAND.yellow}10` }}>
                  <p className="text-sm" style={{ color: theme.textMuted }}>
                    ☕ <strong>Pareto</strong> – Handgeröstete Kaffeebohnen direkt aus Italien. 
                    Perfekt für Siebträger oder Vollautomaten.
                  </p>
                </div>
                <button onClick={() => setShowExtraModal(null)} className="w-full py-4 rounded-full font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                  Auswahl speichern
                </button>
              </>
            )}

            {/* CARD MODAL */}
            {showExtraModal === 'card' && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.pink}15` }}>
                    <FileText className="w-6 h-6" style={{ color: BRAND.pink }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black">{t.extras.card}</h3>
                    <p className="text-sm" style={{ color: theme.textMuted }}>A6-Postkarte mit deiner Nachricht</p>
                  </div>
                </div>
                <textarea 
                  value={cardMessage}
                  onChange={(e) => setCardMessage(e.target.value.slice(0, 300))}
                  placeholder={t.extras.cardPlaceholder}
                  rows={5}
                  className="w-full p-4 rounded-xl outline-none resize-none mb-2"
                  style={{ 
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', 
                    border: `2px solid ${cardMessage.length > 0 ? BRAND.pink : theme.border}`,
                    color: theme.text
                  }}
                />
                <div className="flex justify-between text-sm mb-6" style={{ color: theme.textMuted }}>
                  <span>{cardMessage.length} / 300 Zeichen</span>
                  {cardMessage.length >= 280 && (
                    <span style={{ color: BRAND.pink }}>Fast voll!</span>
                  )}
                </div>
                <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: `${BRAND.pink}10` }}>
                  <p className="text-sm" style={{ color: theme.textMuted }}>
                    💌 Deine Nachricht wird auf eine hochwertige A6-Postkarte gedruckt und der Box beigelegt.
                  </p>
                </div>
                <button onClick={() => setShowExtraModal(null)} className="w-full py-4 rounded-full font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                  Nachricht speichern
                </button>
              </>
            )}

            {/* DIPS MODAL - Mit Mengenauswahl */}
            {showExtraModal === 'dips' && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.turquoise}15` }}>
                    <Droplets className="w-6 h-6" style={{ color: BRAND.turquoise }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black">{t.extras.dips}</h3>
                    <p className="text-sm" style={{ color: theme.textMuted }}>{t.extras.selectDips}</p>
                  </div>
                </div>
                <div className="space-y-3 mb-6 max-h-[300px] overflow-y-auto">
                  {DIP_OPTIONS.map(dip => (
                    <div key={dip.id} 
                      className="p-4 rounded-xl transition-all flex items-center justify-between"
                      style={{ 
                        backgroundColor: dipQuantities[dip.id] > 0 ? `${BRAND.turquoise}10` : (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'), 
                        border: `2px solid ${dipQuantities[dip.id] > 0 ? BRAND.turquoise : theme.border}`
                      }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: dip.color, border: dip.id === 'white_chocolate' ? `1px solid ${theme.border}` : 'none' }} />
                        <div>
                          <span className="font-bold">{dip.name[language]}</span>
                          <p className="text-xs" style={{ color: BRAND.turquoise }}>0,50€ {t.extras.perPiece}</p>
                        </div>
                      </div>
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => setDipQuantities(prev => ({ ...prev, [dip.id]: Math.max(0, (prev[dip.id] || 0) - 1) }))}
                          className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:scale-110"
                          style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                          disabled={!dipQuantities[dip.id]}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-black text-lg">{dipQuantities[dip.id] || 0}</span>
                        <button 
                          onClick={() => setDipQuantities(prev => ({ ...prev, [dip.id]: (prev[dip.id] || 0) + 1 }))}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-white transition-all hover:scale-110"
                          style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Summe */}
                <div className="p-4 rounded-xl mb-6 flex items-center justify-between" style={{ backgroundColor: `${BRAND.turquoise}10` }}>
                  <div>
                    <p className="text-sm" style={{ color: theme.textMuted }}>
                      🍫 {Object.values(dipQuantities).reduce((sum, q) => sum + q, 0)} Dip(s) in kleinen Behältern
                    </p>
                  </div>
                  <span className="text-xl font-black" style={{ color: BRAND.turquoise }}>
                    {calculateDipPrice().toFixed(2)}€
                  </span>
                </div>
                <button onClick={() => setShowExtraModal(null)} className="w-full py-4 rounded-full font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                  {language === 'de' ? 'Auswahl speichern' : 'Save selection'}
                </button>
              </>
            )}

            {/* BRANDING MODAL */}
            {showExtraModal === 'branding' && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                    <Building2 className="w-6 h-6" style={{ color: BRAND.yellow }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black">{t.extras.branding}</h3>
                    <p className="text-sm" style={{ color: theme.textMuted }}>{t.extras.uploadLogo}</p>
                  </div>
                </div>
                
                {/* Upload Area */}
                <div 
                  className="relative border-2 border-dashed rounded-2xl p-8 mb-6 text-center cursor-pointer transition-all hover:border-opacity-70"
                  style={{ borderColor: brandingLogo ? BRAND.yellow : theme.border }}
                  onClick={() => document.getElementById('logo-upload').click()}
                >
                  <input 
                    id="logo-upload"
                    type="file" 
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setBrandingLogo(file);
                        const reader = new FileReader();
                        reader.onloadend = () => setBrandingLogoPreview(reader.result);
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  {brandingLogoPreview ? (
                    <div className="flex flex-col items-center">
                      <img src={brandingLogoPreview} alt="Logo Preview" className="max-h-32 max-w-full object-contain mb-3 rounded-lg" />
                      <p className="text-sm font-medium" style={{ color: BRAND.yellow }}>✓ {brandingLogo.name}</p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setBrandingLogo(null); setBrandingLogoPreview(null); }}
                        className="mt-2 text-xs text-red-500 font-medium"
                      >
                        Logo entfernen
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${BRAND.yellow}15` }}>
                        <Upload className="w-8 h-8" style={{ color: BRAND.yellow }} />
                      </div>
                      <p className="font-bold mb-1">Logo hier ablegen</p>
                      <p className="text-sm" style={{ color: theme.textMuted }}>oder klicken zum Auswählen</p>
                      <p className="text-xs mt-2" style={{ color: theme.textMuted }}>PNG, JPG oder SVG · Max. 5MB</p>
                    </>
                  )}
                </div>

                <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: `${BRAND.yellow}10` }}>
                  <p className="text-sm" style={{ color: theme.textMuted }}>
                    🏢 Dein Firmenlogo wird als hochwertiger Sticker auf die Donut-Box gedruckt. 
                    Ideal für Mitarbeiterevents oder Kundengeschenke.
                  </p>
                </div>
                <button onClick={() => setShowExtraModal(null)} className="w-full py-4 rounded-full font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}>
                  {brandingLogo ? 'Logo speichern' : 'Ohne Logo fortfahren'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* GLÜCKSRAD POPUP - ENHANCED GAMIFICATION */}
      {showSpinWheel && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          {/* Animated Background with Particles */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-lg" onClick={() => !wheelSpinning && !wheelResult && setShowSpinWheel(false)}>
            {/* Floating Particles */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-pulse pointer-events-none"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  backgroundColor: [BRAND.turquoise, BRAND.pink, BRAND.yellow][i % 3],
                  opacity: 0.3,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>
          
          <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            {/* Glow Effect Behind Modal */}
            <div className="absolute -inset-4 rounded-[40px] opacity-50 blur-2xl"
              style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink}, ${BRAND.yellow})` }} />
            
            {/* Main Modal */}
            <div className="relative rounded-3xl overflow-hidden"
              style={{ 
                backgroundColor: '#0a0a0a', 
                border: `3px solid transparent`,
                backgroundImage: `linear-gradient(#0a0a0a, #0a0a0a), linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink}, ${BRAND.yellow})`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box'
              }}>
              
              {/* Close Button */}
              {!wheelSpinning && !wheelResult && (
                <button 
                  onClick={() => setShowSpinWheel(false)} 
                  className="absolute top-4 right-4 z-20 p-2 rounded-full transition-all hover:scale-110 hover:rotate-90"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                >
                  <X className="w-5 h-5" />
                </button>
              )}

              {/* Header with Animation */}
              <div className="relative text-center pt-6 pb-4 px-6 overflow-hidden">
                {/* Animated Stars Background */}
                <div className="absolute inset-0">
                  <Sparkles className="absolute top-2 left-6 w-8 h-8 animate-bounce" style={{ color: BRAND.yellow, animationDelay: '0s' }} />
                  <Star className="absolute top-6 right-8 w-6 h-6 animate-bounce" style={{ color: BRAND.pink, animationDelay: '0.3s' }} />
                  <Sparkles className="absolute bottom-2 left-12 w-5 h-5 animate-bounce" style={{ color: BRAND.turquoise, animationDelay: '0.6s' }} />
                  <Star className="absolute bottom-4 right-16 w-4 h-4 animate-bounce" style={{ color: BRAND.yellow, animationDelay: '0.9s' }} />
                  <Gift className="absolute top-4 left-1/4 w-5 h-5 animate-pulse" style={{ color: BRAND.pink, opacity: 0.5 }} />
                  <Gift className="absolute bottom-2 right-1/4 w-4 h-4 animate-pulse" style={{ color: BRAND.turquoise, opacity: 0.5 }} />
                </div>
                
                {/* Trophy Icon */}
                <div className="relative w-16 h-16 mx-auto mb-3">
                  <div className="absolute inset-0 rounded-full animate-ping opacity-30"
                    style={{ backgroundColor: BRAND.yellow }} />
                  <div className="relative w-full h-full rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${BRAND.yellow}, ${BRAND.pink})` }}>
                    <span className="text-3xl">🎰</span>
                  </div>
                </div>
                
                <h2 className="text-2xl font-black mb-1" style={{ 
                  background: `linear-gradient(135deg, ${BRAND.yellow}, ${BRAND.pink}, ${BRAND.turquoise})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {language === 'de' ? 'GLÜCKSRAD' : 'LUCKY WHEEL'}
                </h2>
                <p className="text-xs font-medium" style={{ color: BRAND.turquoise }}>
                  {language === 'de' ? '100% Gewinnchance!' : '100% Win Rate!'}
                </p>
              </div>

              <div className="px-6 pb-6">
                {!wheelResult ? (
                  <>
                    {/* Enhanced Wheel */}
                    <div className="relative w-80 h-80 mx-auto mb-6">
                      {/* Outer Glow Ring */}
                      <div className="absolute -inset-4 rounded-full opacity-40 blur-xl animate-pulse"
                        style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink}, ${BRAND.yellow})` }} />
                      
                      {/* Decorative Lights Around Wheel */}
                      {[...Array(20)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: wheelSpinning 
                              ? (i % 2 === 0 ? BRAND.yellow : BRAND.pink) 
                              : (i % 3 === 0 ? BRAND.turquoise : i % 3 === 1 ? BRAND.pink : BRAND.yellow),
                            top: '50%',
                            left: '50%',
                            transform: `rotate(${i * 18}deg) translateY(-155px)`,
                            transformOrigin: '0 0',
                            boxShadow: `0 0 12px currentColor`,
                            animation: wheelSpinning ? `pulse 0.3s ease-in-out ${i * 0.05}s infinite` : 'none'
                          }}
                        />
                      ))}
                      
                      {/* Main Wheel */}
                      <div 
                        className="absolute inset-4 rounded-full shadow-2xl overflow-hidden"
                        style={{ 
                          transform: wheelSpinning ? `rotate(${1800 + Math.random() * 720}deg)` : 'rotate(0deg)',
                          transition: wheelSpinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
                          boxShadow: `0 0 40px ${BRAND.turquoise}50, inset 0 0 40px rgba(0,0,0,0.6)`,
                          background: `conic-gradient(
                            from 0deg,
                            ${BRAND.turquoise} 0deg 60deg,
                            ${BRAND.pink} 60deg 120deg,
                            ${BRAND.yellow} 120deg 180deg,
                            #FF6B6B 180deg 240deg,
                            ${BRAND.turquoise} 240deg 300deg,
                            ${BRAND.pink} 300deg 360deg
                          )`
                        }}
                      >
                        
                        {/* Segment Labels - Große Zahlen */}
                        {[
                          { text: '5%', subtext: 'RABATT', angle: 30 },
                          { text: '10%', subtext: 'RABATT', angle: 90 },
                          { text: '5%', subtext: 'RABATT', angle: 150 },
                          { text: '15%', subtext: 'MEGA!', angle: 210 },
                          { text: '5%', subtext: 'RABATT', angle: 270 },
                          { text: '10%', subtext: 'RABATT', angle: 330 },
                        ].map((segment, i) => {
                          const radius = 80;
                          const rad = (segment.angle - 90) * (Math.PI / 180);
                          const x = Math.cos(rad) * radius;
                          const y = Math.sin(rad) * radius;
                          
                          return (
                            <div
                              key={i}
                              className="absolute text-white font-black text-center pointer-events-none"
                              style={{
                                top: '50%',
                                left: '50%',
                                width: '50px',
                                marginLeft: '-25px',
                                marginTop: '-15px',
                                transform: `translate(${x}px, ${y}px)`,
                                textShadow: '1px 1px 3px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.7)'
                              }}
                            >
                              <div className="text-lg font-black leading-none">{segment.text}</div>
                              <div className="text-[5px] font-bold tracking-wider opacity-70 mt-0.5">{segment.subtext}</div>
                            </div>
                          );
                        })}
                        
                        {/* Inner Ring */}
                        <div className="absolute inset-[30%] rounded-full"
                          style={{ 
                            background: 'linear-gradient(135deg, #2a2a3e, #1a1a2e)',
                            boxShadow: 'inset 0 0 25px rgba(0,0,0,0.9), 0 0 15px rgba(0,0,0,0.5)'
                          }} />
                      </div>
                      
                      {/* Center Button */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-24 h-24 rounded-full flex items-center justify-center shadow-2xl"
                          style={{ 
                            background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`,
                            boxShadow: `0 0 40px ${BRAND.turquoise}70, inset 0 2px 10px rgba(255,255,255,0.3)`
                          }}>
                          <span className="text-4xl">{wheelSpinning ? '🎰' : '🍩'}</span>
                        </div>
                      </div>
                      
                      {/* Pointer Arrow - Größer und auffälliger */}
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
                        <div className="relative">
                          <div className="w-0 h-0"
                            style={{ 
                              borderLeft: '18px solid transparent',
                              borderRight: '18px solid transparent',
                              borderTop: `40px solid ${BRAND.yellow}`,
                              filter: `drop-shadow(0 6px 12px ${BRAND.yellow}80)`
                            }} />
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full animate-pulse"
                            style={{ backgroundColor: 'white', boxShadow: '0 0 15px white, 0 0 30px white' }} />
                        </div>
                      </div>
                    </div>

                    {/* Prizes Preview - Verbessert */}
                    <div className="flex justify-center gap-3 mb-5 flex-wrap">
                      {[
                        { text: '5%', color: BRAND.turquoise },
                        { text: '10%', color: BRAND.pink },
                        { text: '15%', color: '#FF6B6B' }
                      ].map((prize, i) => (
                        <div key={i} className="px-4 py-2 rounded-full text-sm font-black flex items-center gap-2"
                          style={{ 
                            backgroundColor: `${prize.color}25`,
                            color: prize.color,
                            border: `2px solid ${prize.color}50`
                          }}>
                          <span className="text-lg">🎁</span>
                          <span>{prize.text} Rabatt</span>
                        </div>
                      ))}
                    </div>

                    {/* Email Input */}
                    <div className="relative mb-4">
                      <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                        <Mail className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                      </div>
                      <input 
                        type="email"
                        value={wheelEmail}
                        onChange={(e) => setWheelEmail(e.target.value)}
                        placeholder={language === 'de' ? 'Deine E-Mail für den Gewinn' : 'Your email for the prize'}
                        disabled={wheelSpinning}
                        className="w-full p-4 pl-12 rounded-xl text-center outline-none transition-all focus:ring-2"
                        style={{ 
                          backgroundColor: 'rgba(255,255,255,0.05)', 
                          border: `2px solid ${wheelEmail ? BRAND.turquoise : theme.border}`,
                          color: theme.text
                        }}
                      />
                      {wheelEmail && (
                        <div className="absolute inset-y-0 right-4 flex items-center">
                          <Check className="w-5 h-5" style={{ color: BRAND.turquoise }} />
                        </div>
                      )}
                    </div>

                    {/* Spin Button */}
                    <button
                      onClick={spinWheel}
                      disabled={!wheelEmail || wheelSpinning}
                      className="relative w-full py-4 rounded-xl font-black text-white text-lg transition-all disabled:opacity-50 overflow-hidden group"
                      style={{ 
                        background: wheelSpinning 
                          ? 'rgba(255,255,255,0.1)' 
                          : `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`,
                      }}
                    >
                      {/* Button Shine Effect */}
                      {!wheelSpinning && (
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)' }} />
                      )}
                      <span className="relative flex items-center justify-center gap-2">
                        {wheelSpinning ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            {language === 'de' ? 'Viel Glück...' : 'Good luck...'}
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5" />
                            {language === 'de' ? 'JETZT DREHEN!' : 'SPIN NOW!'}
                            <Sparkles className="w-5 h-5" />
                          </>
                        )}
                      </span>
                    </button>

                    {/* Trust Text */}
                    <p className="text-center text-xs mt-3" style={{ color: theme.textMuted }}>
                      🔒 {language === 'de' ? 'Deine Daten sind sicher. Kein Spam!' : 'Your data is safe. No spam!'}
                    </p>
                  </>
                ) : (
                  /* Enhanced Result Display */
                  <div className="text-center py-4">
                    {/* Confetti Effect */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      {[...Array(30)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-3 h-3 animate-bounce"
                          style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            backgroundColor: [BRAND.turquoise, BRAND.pink, BRAND.yellow][i % 3],
                            borderRadius: i % 2 === 0 ? '50%' : '0',
                            transform: `rotate(${Math.random() * 360}deg)`,
                            animationDelay: `${i * 0.1}s`,
                            animationDuration: `${1 + Math.random()}s`
                          }}
                        />
                      ))}
                    </div>
                    
                    {/* Trophy Animation */}
                    <div className="relative w-28 h-28 mx-auto mb-4">
                      <div className="absolute inset-0 rounded-full animate-ping opacity-30"
                        style={{ backgroundColor: BRAND.yellow }} />
                      <div className="absolute inset-2 rounded-full animate-pulse"
                        style={{ background: `linear-gradient(135deg, ${BRAND.yellow}40, ${BRAND.pink}40)` }} />
                      <div className="relative w-full h-full rounded-full flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${BRAND.yellow}, ${BRAND.pink})` }}>
                        <span className="text-5xl">🎉</span>
                      </div>
                    </div>
                    
                    <h3 className="text-3xl font-black mb-2" style={{ 
                      background: `linear-gradient(135deg, ${BRAND.yellow}, ${BRAND.pink})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {language === 'de' ? 'GEWONNEN!' : 'YOU WON!'}
                    </h3>
                    
                    <p className="text-5xl font-black mb-4" style={{ 
                      background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {wheelResult.text}
                    </p>
                    
                    {/* Code Card */}
                    <div className="relative p-5 rounded-2xl mb-4 overflow-hidden"
                      style={{ 
                        background: `linear-gradient(135deg, ${BRAND.turquoise}15, ${BRAND.pink}15)`,
                        border: `2px dashed ${BRAND.turquoise}`
                      }}>
                      <div className="absolute top-2 right-2">
                        <button 
                          onClick={() => {
                            navigator.clipboard.writeText(wheelResult.code);
                            toast.success('Code kopiert!');
                          }}
                          className="p-2 rounded-lg transition-all hover:scale-110"
                          style={{ backgroundColor: `${BRAND.turquoise}20` }}
                        >
                          <FileText className="w-4 h-4" style={{ color: BRAND.turquoise }} />
                        </button>
                      </div>
                      <p className="text-xs mb-2" style={{ color: theme.textMuted }}>
                        {language === 'de' ? 'Dein persönlicher Rabattcode:' : 'Your personal discount code:'}
                      </p>
                      <p className="text-3xl font-black tracking-[0.3em]" style={{ color: BRAND.turquoise }}>
                        {wheelResult.code}
                      </p>
                      <p className="text-xs mt-2" style={{ color: BRAND.pink }}>
                        ✉️ {language === 'de' ? 'Auch per E-Mail gesendet!' : 'Also sent by email!'}
                      </p>
                    </div>
                    
                    {/* CTA Button - Direkt zum Checkout */}
                    <button
                      onClick={() => {
                        setShowSpinWheel(false);
                        setShowCheckout(true);
                      }}
                      className="w-full py-4 rounded-xl font-black text-white text-lg transition-all hover:scale-[1.02]"
                      style={{ 
                        background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`,
                        boxShadow: `0 10px 30px ${BRAND.turquoise}30`
                      }}
                    >
                      {language === 'de' ? 'WEITER ZUR KASSE 🍩' : 'CONTINUE TO CHECKOUT 🍩'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Cookie Consent Banner - DSGVO */}
      <CookieConsent
        location="bottom"
        buttonText="Akzeptieren"
        declineButtonText="Ablehnen"
        enableDeclineButton
        cookieName="fancy_donuts_consent"
        style={{ 
          background: "#0a0a0a", 
          borderTop: "1px solid rgba(255,255,255,0.1)",
          padding: "16px 20px",
          alignItems: "center"
        }}
        buttonStyle={{ 
          background: "linear-gradient(135deg, #81CBD6, #F39DB2)", 
          color: "white", 
          fontSize: "14px",
          fontWeight: "bold",
          borderRadius: "8px",
          padding: "10px 20px"
        }}
        declineButtonStyle={{
          background: "rgba(255,255,255,0.1)",
          color: "white",
          fontSize: "14px",
          fontWeight: "bold",
          borderRadius: "8px",
          padding: "10px 20px"
        }}
        expires={365}
        data-testid="cookie-consent-banner"
      >
        <span style={{ fontSize: "14px" }}>
          🍪 Diese Website verwendet Cookies, um Ihnen das beste Erlebnis zu bieten.{" "}
          <a href="/datenschutz" style={{ color: "#81CBD6", textDecoration: "underline" }}>
            Mehr erfahren
          </a>
        </span>
      </CookieConsent>
    </div>
  );
}

export default App;
